import { closedCardClass } from '@/components/Workflow/closedAppearance';
import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { TypingDots as SharedTypingDots } from '@/components/chat/TypingDots';
import { OPEN_RECENT_VIEWS_MENU_EVENT } from '@/components/SAM/samActionTypes';
import TaskList from '@/components/Tasks/TaskList';
import TaskDetailPanel, { type TaskDetailTab } from '@/components/Tasks/TaskDetailPanel';
import { NoteDetailContent } from '@/components/notes/NoteDetailContent';
import { useDeleteNoteMutation } from '@/redux/internal-services/notesApi';
import WorkspaceMeetingList from '@/components/Meetings/WorkspaceMeetingList';
import WorkspacePropertyList from '@/components/Properties/WorkspacePropertyList';
import MeetingDetailPanel from '@/components/Meetings/MeetingDetailPanel';
import WorkflowCard from '@/components/Workflow/WorkflowCard';
import { StageRail } from '@/components/Workflow/StageRail';
import { relativeTime } from '@/components/notes/noteUtils';

import {
  useCreateEngagementMutation,
  useListAvailableTransitionsQuery,
  useGetWorkflowCustomizationQuery,
  useGetEngagementWorkflowStateQuery,
  useGetEngagementActivityQuery,
  useGetEngagementByIdQuery,
  type EngagementActivity,
} from '@/redux/internal-services/workflowEngineApi';
import { useListCatalogueEntriesQuery } from '@/redux/internal-services/platformAdminApi';
import { useGetConfigurationQuery } from '@/redux/internal-services/onboardingApi';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { workspaceApi, refreshWorkspaceModule } from '@/redux/internal-services/workspaceApi';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, useSamSectionContext } from '@/hooks';
import { WORKSPACE_QUERY_OPTIONS } from '@/components/chat/workspaceQueryOptions';
import {
  useGetTaskByIdQuery, useGetMeetingByIdQuery, useGetPropertyDetailQuery,
  useGetPropertyMediaQuery,
  useGetPropertyMeasurementsQuery, useGetPropertyNotesQuery, useGetPropertyContactsQuery,
  useUploadMediaFileMutation, useAttachPropertyMediaMutation,
  useGetTasksListQuery, useGetMeetingsListQuery,
  useClaimTaskMutation, type TaskFull, type MeetingFull, type PropertyContact,
} from '@/redux/internal-services/workspaceApiExtensions';
import PropertyMeasurementsSection from '@/components/Properties/PropertyMeasurementsSection';
import PropertyNotesSection from '@/components/Properties/PropertyNotesSection';
import TaskCard, { ContextTypeIcon } from '@/components/Tasks/TaskCard';
import WorkspaceMeetingCard from '@/components/Meetings/WorkspaceMeetingCard';
import { Spinner } from '@/components/UI/Spinner';
import { Button } from '@/components/UI/Button';
import { Chip } from '@/components/UI/Chip';
import { Badge } from '@/components/UI/Badge';
import { WorkspaceHeader } from '@/components/UI/WorkspaceHeader';
import { IconButton } from '@/components/UI/IconButton';
import { Modal } from '@/components/UI/Modal';
import { fieldClass } from '@/components/UI/fieldStyles';
import { ExpandChevron } from '@/components/UI/SwipeHint';
import { useCardCatalogueState } from '@/components/SAM/moduleTools';
import { resolveCardDefinition } from '@/components/SAM/cards/cardContext';
import { CardSection } from '@/components/UI/CardSection';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ProposalSurfaceInteractPayload, SamFacet, SamModule } from '@/components/SAM/samActionTypes';
import { humanizeLabel, sv, cardId, cardInfo, fmtDate, type Rec } from '@/components/chat/ChatPills';
import { CPill } from '@/components/chat/ChatPills';
import { viewFallbackLabel, type RecentEntry } from '@/components/chat/recentViews';
import { ModuleIcon } from '@/components/chat/moduleIcons';
import CalendarPage from '@/pages/calendar/CalendarPage';
import { NotificationItem } from '@/components/Notifications';
import { useGetInboxQuery, useGetInboxCountsQuery, type InAppNotificationOut } from '@/redux/internal-services/notificationsApi';
import ApprovalDetailPanel from '@/components/Workflow/ApprovalDetailPanel';
import { useGetApprovalByIdQuery } from '@/redux/internal-services/workflowEngineApi';

// ─── types ────────────────────────────────────────────────────────────────────

export interface CardsMessage {
  kind: 'cards';
  id: string;
  module: SamModule;
  records: Record<string, unknown>[];
  filter?: Record<string, unknown>;
  facets?: SamFacet[];
  /** LLM-authored heading shown in the workspace header (falls back to the module name). */
  title?: string;
  /** LLM-authored one-line summary, revealed via the workspace "⋯" menu. */
  caption?: string;
  /** Set on a reload-restored placeholder whose rows still need to be fetched. */
  hydrate?: boolean;
  /** Restore only: the detail record to reopen on top of the hydrated list (a `screen_kept` turn). */
  hydrateRecordId?: string;
  /** Turn id that produced this view — used to show the matching assistant reply above the cards. */
  messageId?: string;
  /** Card-catalogue id of the list/panel card this view renders (e.g. `task.list`). */
  cardDefinitionId?: string;
  /** Server card instance (cards_version 2) — set when the view came from a SAM `card.open`. */
  cardInstanceId?: string | null;
}

export type OnSave = (module: SamModule, id: string, draft: Rec) => void;
export type OnOpenChange = (id: string | null) => void;
export type OnFacet = (module: SamModule, facet: SamFacet) => void;

// The "⋯" menu's entries are `RecentEntry` (see `@/components/chat/recentViews`) — one row per
// DISTINCT view, backed by the `workspace_recent_views` collection. What used to be flag fields
// on the old local-only `ViewHistoryEntry` (communicationId / engagementId / workflowSettings /
// workflowList) is now one `contextRef: {type, id}`, so `replayView` switches on `contextRef.type`.
export type { RecentEntry } from '@/components/chat/recentViews';

/** A workspace view the user opened with no agent turn behind it — reported to `useRecentViews`
 *  so it joins the same "⋯" menu as the agent's. */
export interface UserViewReport {
  module: SamModule;
  params?: Record<string, unknown>;
  title?: string;
  subtitle?: string;
  /** The record the view is bound to (a property/engagement/…), when it isn't a plain list. */
  recordId?: string | null;
}

/** Carries `onUserView` to deeply-nested cards (the engagement `+`, a detail panel) without
 *  threading the prop through every intermediate component. */
const UserViewContext = React.createContext<((view: UserViewReport) => void) | undefined>(undefined);

/** Carries the lifted pendingEngagement (see its declaration in UXView) down to
 *  WorkspacePropertyDetail's own engagement pickers (header dropdown, card-picker list) without
 *  threading it as a prop through this file's three separate WorkspacePropertyDetail call sites.
 *  Every explicit engagement pick — property-list row, header dropdown, or picker list — must
 *  write here so the choice survives the mobile chat<->workspace toggle remounting this tree;
 *  writing only to the picker's own local state (the pre-fix behavior) let it revert to whatever
 *  engagement SAM last pointed at the moment the user toggled back. */
const PendingEngagementContext = React.createContext<{
  pendingEngagement: { propertyId: string; engagementId: string } | null;
  setPendingEngagement: (v: { propertyId: string; engagementId: string } | null) => void;
} | undefined>(undefined);

// ─── Reply strip (SAM's latest message above the workspace card on mobile) ───────

/** Three-dot "SAM is replying" indicator, sized for the compact reply-strip contexts here —
 *  same shared component the chat thread's TypingIndicator uses (ChatThread.tsx). */
const TypingDots = () => <SharedTypingDots dotClassName="w-1.5 h-1.5 bg-accent" />;

export function ReplyStrip({ text, onExpand, isWaiting }: { text: string; onExpand?: () => void; isWaiting?: boolean }) {
  if (!text && !isWaiting) return null;
  return (
    <div
      className={`flex items-start gap-2 px-4 py-2 border-b border-ui-border bg-ui-subtle text-ui-text2 text-xs shrink-0 ${isWaiting ? '' : 'cursor-pointer'}`}
      onClick={isWaiting ? undefined : onExpand}
    >
      {isWaiting ? <TypingDots /> : <span className="flex-1 line-clamp-2">{text}</span>}
      {onExpand && !isWaiting && <span className="shrink-0 underline">View</span>}
    </div>
  );
}

// ─── History menu (the "⋯" in the workspace header) ─────────────────────────────

export function HistoryMenu({ history, caption, onReplay, hasMore, onLoadMore }: {
  history: RecentEntry[];
  caption?: string;
  onReplay?: (e: RecentEntry) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const currentUserId = useAppSelector((st) => st.sessionState.userId) ?? undefined;

  // SAM's list_recent_views ("show me my recent views") opens this dropdown too.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_RECENT_VIEWS_MENU_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_RECENT_VIEWS_MENU_EVENT, onOpen);
  }, []);

  // Watch sentinel div with IntersectionObserver — triggers onLoadMore when scrolled into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !onLoadMore || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) onLoadMore(); },
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, hasMore, onLoadMore]);

  // The "⋯" lists every distinct view this session — title + caption — clickable to re-open.
  if (!history.length && !caption) return null;
  return (
    <div className="relative">
      <IconButton
        label={caption || 'Session actions'}
        size="sm"
        active={open}
        tone="accent"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="text-lg leading-none"
      >
        &#8943;
      </IconButton>
      {open && (
        <>
          <div className="fixed inset-0 z-dropdown" onClick={() => setOpen(false)} aria-hidden />
          <div className="no-scrollbar absolute right-0 top-[110%] z-dropdown max-h-96 w-[min(320px,calc(100vw-16px))] overflow-y-auto rounded-control border border-ui-border bg-ui-card py-1 shadow-pop">
            <p className="eyebrow px-3 py-1.5">This session</p>
            {history.length > 0 ? (
              <>
                {/* No client-side dedupe pass: entries arrive already deduped by `recentViewKey`
                    (module + kind + bound record + filter). The old filter here compared TITLES,
                    which both merged genuinely different views that happened to share a heading
                    and failed to merge identical views the agent captioned differently. */}
                {history.map((e) => (
                  <button
                    key={e.key}
                    onClick={() => { onReplay?.(e); setOpen(false); }}
                    // The row shows a compact relative age; the exact time is on hover, since
                    // "now"/"2m" collapses everything recent into the same label.
                    title={absTime(e.lastAccessedAt)}
                    className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-ui-hover transition"
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span title={humanizeLabel(e.cardType)}>
                          <ModuleIcon module={e.cardType} className="text-ui-text3" />
                        </span>
                        <span className="truncate text-sm font-medium text-ui-text">
                          {e.title || viewFallbackLabel(e, currentUserId)}
                        </span>
                      </span>
                      <span className="flex-shrink-0 text-xs text-ui-text3">{relTime(e.lastAccessedAt)}</span>
                    </div>
                    {e.subtitle && <span className="text-xs leading-snug text-ui-text2">{e.subtitle}</span>}
                  </button>
                ))}
                {hasMore && (
                  <div ref={sentinelRef} className="flex justify-center py-2">
                    <span className="text-xs text-ui-text3">Loading more…</span>
                  </div>
                )}
              </>
            ) : (
              caption && <p className="px-3 py-2 text-xs leading-relaxed text-ui-text2">{caption}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Exact local date + time an entry was last opened — the "⋯" row's hover tooltip. */
function absTime(ts: number): string {
  if (!ts) return '';
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function relTime(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

// ─── WorkspaceRefreshButton ───────────────────────────────────────────────────
// Sits immediately before the HistoryMenu (⋯) in the workspace header.
// Dispatches refreshWorkspaceModule which invalidates the right RTK Query tags
// for the current module + open detail id, without touching tag descriptors in UI code.

function WorkspaceRefreshButton({ module, openId, onRefreshChange }: {
  module: SamModule;
  openId?: string | null;
  onRefreshChange?: (refreshing: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    onRefreshChange?.(true);
    dispatch(refreshWorkspaceModule(module, openId));
    setTimeout(() => {
      setIsRefreshing(false);
      onRefreshChange?.(false);
    }, 1500);
  };

  return (
    <IconButton
      label={isRefreshing ? 'Refreshing…' : 'Refresh'}
      size="sm"
      active={isRefreshing}
      tone="accent"
      onClick={handleRefresh}
    >
      <svg
        width="14" height="14" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round"
        aria-hidden
        className={isRefreshing ? 'animate-spin' : ''}
      >
        <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
        <path d="M3 3v5h5"/>
        <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
        <path d="M16 16h5v5"/>
      </svg>
    </IconButton>
  );
}

// ─── FacetBar ─────────────────────────────────────────────────────────────────

function FacetBar({ facets, activeKey, onPick }: { facets: SamFacet[]; activeKey: string; onPick: (f: SamFacet) => void }) {
  return (
    <div className="mb-2.5 flex flex-wrap gap-1.5">
      {facets.map(f => (
        <Chip key={f.key} selected={f.key === activeKey} onClick={() => onPick(f)} className="whitespace-nowrap px-3 py-1">
          {f.label} ({f.count})
        </Chip>
      ))}
    </div>
  );
}

// ─── InlineCards ─────────────────────────────────────────────────────────────

function IcoPhone({ className }: { className?: string }) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  );
}

function IcoMail({ className }: { className?: string }) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/>
    </svg>
  );
}

// Mirrors UsersPage.tsx's STATUS_VARIANT so a user's status reads the same color
// in the platform admin table and here in SAM's inline card.
const USER_STATUS_TONE: Record<string, 'success' | 'accent' | 'neutral' | 'warning' | 'danger'> = {
  active: 'success', invited: 'accent', inactive: 'neutral', suspended: 'warning', deleted: 'danger',
};

const CONTACT_STATUS_TONE: Record<string, 'success' | 'steel' | 'neutral'> = {
  active: 'success', inactive: 'steel', archived: 'neutral', deleted: 'neutral',
};

export function InlineCards({ module, records, facets, openedId, onOpenChange, onFacet }: {
  module: SamModule; records: Rec[]; facets?: SamFacet[]; onSave?: OnSave;
  openedId?: string | null; onOpenChange?: OnOpenChange; onFacet?: OnFacet;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeFacet, setActiveFacet] = useState<string>('all');

  useEffect(() => {
    if (openedId !== undefined) setSelectedId(openedId);
  }, [openedId]);

  const select = (id: string | null) => { setSelectedId(id); onOpenChange?.(id); };
  // Contacts, users, and roles have too little extra data to justify a dedicated detail page
  // (just raw id/timestamp fields) — they expand in place instead, like a TaskCard row.
  const isPerson = module === 'contacts' || module === 'users';
  const isRole = module === 'roles';

  const pickFacet = (f: SamFacet) => { setActiveFacet(f.key); onFacet?.(module, f); };

  return (
    <div className="mt-1 rounded-card p-3">
      <p className="mb-2 pl-0.5 text-micro font-semibold uppercase tracking-eyebrow text-ui-text3">
        {module} &middot; {records.length}
      </p>
      {facets && facets.length > 1 && (
        <FacetBar facets={facets} activeKey={activeFacet} onPick={pickFacet} />
      )}
      {records.map((r, idx) => {
            const { title, sub, priority, status, location } = cardInfo(module, r) as ReturnType<typeof cardInfo> & { location?: string };
            const id = cardId(module, r);

            if (isRole) {
              const isOpen = selectedId === id;
              const mappings = Array.isArray(r.system_role_mappings) ? (r.system_role_mappings as string[]).filter(Boolean) : [];
              return (
                <div key={id} className="mb-2 overflow-hidden rounded-control border border-ui-border bg-ui-card transition-colors hover:border-accent/30">
                  <div
                    onClick={() => select(isOpen ? null : id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(isOpen ? null : id); } }}
                    className="flex w-full cursor-pointer items-center gap-2.5 px-3.5 py-3 text-left"
                  >
                    <span className="shrink-0 text-xs text-ui-text3">{idx + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ui-text">{title}</p>
                      {sub && <p className="truncate pl-px text-xs text-ui-text2">{sub}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {priority && <Badge variant="steel">{humanizeLabel(priority)}</Badge>}
                      {status && <Badge variant={status === 'hidden' ? 'warning' : 'steel'}>{humanizeLabel(status)}</Badge>}
                      <ExpandChevron open={isOpen} className="text-ui-text3" />
                    </div>
                  </div>
                  {isOpen && mappings.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 border-t border-ui-border bg-ui-hover px-3.5 py-3">
                      {mappings.map(m => <Badge key={m} variant="accent">{m}</Badge>)}
                    </div>
                  )}
                </div>
              );
            }

            if (isPerson) {
              const phone = sv(r.primary_phone) || sv(r.phone);
              const email = sv(r.primary_email) || sv(r.email);
              const avatarUrl = sv(r.profile_picture_url);
              const roleNames = Array.isArray(r.role_names) ? (r.role_names as string[]).filter(Boolean) : [];
              const tenantName = sv(r.tenant_name);
              const isOpen = selectedId === id;
              const statusTone = module === 'users'
                ? USER_STATUS_TONE[status.toLowerCase()] ?? 'neutral'
                : CONTACT_STATUS_TONE[status.toLowerCase()] ?? 'accent';
              return (
                <div key={id} className="mb-2 overflow-hidden rounded-control border border-ui-border bg-ui-card transition-colors hover:border-accent/30">
                  <div
                    onClick={() => select(isOpen ? null : id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(isOpen ? null : id); } }}
                    className="flex w-full cursor-pointer items-center gap-3 px-3.5 py-3 text-left"
                  >
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent-text">
                        {initials(title)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ui-text">{title}</p>
                      {sub && <p className="truncate text-xs text-ui-text2">{sub}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {phone && (
                        <a
                          href={`tel:${phone}`}
                          title="Call"
                          onClick={e => e.stopPropagation()}
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-accent/10 text-accent-text transition-colors hover:bg-accent/20"
                        >
                          <IcoPhone />
                        </a>
                      )}
                      {status && <Badge variant={statusTone}>{humanizeLabel(status)}</Badge>}
                      <ExpandChevron open={isOpen} className="text-ui-text3" />
                    </div>
                  </div>
                  {isOpen && (phone || email || roleNames.length > 0 || tenantName) && (
                    <div className="flex flex-col gap-2 border-t border-ui-border bg-ui-hover px-3.5 py-3">
                      {phone && (
                        <a href={`tel:${phone}`} className="flex items-center gap-2 text-sm text-ui-text transition-colors hover:text-accent-text">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent-text"><IcoPhone /></span>
                          {phone}
                        </a>
                      )}
                      {email && (
                        <a href={`mailto:${email}`} className="flex items-center gap-2 text-sm text-ui-text transition-colors hover:text-accent-text">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent-text"><IcoMail /></span>
                          {email}
                        </a>
                      )}
                      {roleNames.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 pl-8">
                          {roleNames.map(r2 => <Badge key={r2} variant="accent">{r2}</Badge>)}
                        </div>
                      )}
                      {tenantName && <p className="pl-8 text-xs text-ui-text3">{tenantName}</p>}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div
                key={id}
                className="mb-2 flex w-full items-start justify-between gap-2.5 rounded-control border border-ui-border bg-ui-subtle px-3.5 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="shrink-0 text-xs text-ui-text3">{idx + 1}</span>
                    <p className="mb-[3px] truncate text-sm font-medium text-ui-text">{title}</p>
                  </div>
                  {location && (
                    <div className={`flex items-center gap-1 pl-[18px] ${sub ? 'mb-0.5' : ''}`}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-accent-text">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span className="truncate text-xs font-medium text-accent-text">{location}</span>
                    </div>
                  )}
                  {sub && <p className="m-0 pl-[18px] text-xs text-ui-text2">{sub}</p>}
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-[5px]">
                  {priority && <CPill label={humanizeLabel(priority)} />}
                  {status && <CPill label={humanizeLabel(status)} />}
                </div>
              </div>
            );
          })}
    </div>
  );
}

// ─── MyTodosList ─────────────────────────────────────────────────────────────

const PRIORITY_DOT: Record<string, { text: string; label: string }> = {
  low:    { text: 'text-ui-text3',      label: 'Low' },
  normal: { text: 'text-accent-text',   label: 'Normal' },
  high:   { text: 'text-warning-text',  label: 'High' },
  urgent: { text: 'text-danger-text',   label: 'Urgent' },
};

// ─── QueueRow — compact card for role-queue tasks (still has the Claim button) ─

function QueueRow({ r, onNavigate, onClaim, claiming, claimError }: {
  r: Rec; onNavigate: () => void; onClaim: () => void; claiming: boolean; claimError: boolean;
}) {
  const title = sv(r['title']) || 'Task';
  const priority = sv(r['priority']);
  const dueOn = sv(r['due_on']);
  const contextLabel = sv(r['context_label']);
  const contextType = sv(r['context_type']);
  const pc = PRIORITY_DOT[priority] ?? PRIORITY_DOT.normal;
  const startStr = dueOn
    ? new Date(dueOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;

  return (
    <>
      <div
        onClick={onNavigate}
        className={`flex cursor-pointer items-start gap-3 rounded-control border border-ui-border bg-ui-subtle px-3 py-2.5 ${claimError ? 'mb-1' : 'mb-2'}`}
      >
        <div className="min-w-0 flex-1">
          <p className="mb-1 truncate text-sm font-semibold text-ui-text">{title}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="accent" className="px-[7px] py-0.5 font-semibold">Queue</Badge>
            {startStr && <span className="text-xs text-ui-text2">{startStr}</span>}
            <span className={`ml-auto text-xs font-medium ${pc.text}`}>{pc.label}</span>
          </div>
          {contextLabel && (
            <div className="mt-[5px] flex items-center gap-1 truncate text-xs text-ui-text2">
              <ContextTypeIcon type={contextType} />{contextLabel}
            </div>
          )}
        </div>
        <Button
          size="xs"
          onClick={(e) => { e.stopPropagation(); onClaim(); }}
          disabled={claiming}
          className="shrink-0 self-center"
        >
          {claiming ? '…' : 'Claim'}
        </Button>
      </div>
      {claimError && (
        <p className="-mt-1 mb-2 pl-0.5 text-xs text-danger-text">
          Couldn't claim this — it may already be taken or not queued for your role.
        </p>
      )}
    </>
  );
}

// ─── MyTodosList ──────────────────────────────────────────────────────────────
// Personal items render as real TaskCard / WorkspaceMeetingCard (swipe + inline expand).
// Queue items use the compact QueueRow (Claim button, navigate-on-click).

function MyTodosList({ records, onSelectTask, onChanged }: {
  records: Rec[];
  onSelectTask: (id: string) => void;
  onSelectMeeting?: (id: string) => void;
  /** Re-fetch this card's rows — they are a one-time snapshot, not an RTK subscription. */
  onChanged?: () => void;
}) {
  const [claimTask, { isLoading: claiming }] = useClaimTaskMutation();
  const [claimedIds, setClaimedIds] = useState<Set<string>>(new Set());
  const [claimErrorId, setClaimErrorId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const currentUserId = useAppSelector((s) => s.sessionState.userId) ?? '';

  const handleDismissed = (id: string) => setDismissedIds(prev => new Set(prev).add(id));

  const personal = records.filter(r => sv(r['source']) !== 'queue' && !dismissedIds.has(sv(r['item_id'])));
  const queue = records.filter(r => sv(r['source']) === 'queue' && !claimedIds.has(sv(r['item_id'])));

  const handleClaim = async (taskId: string) => {
    setClaimErrorId(null);
    try {
      await claimTask(taskId).unwrap();
      setClaimedIds(prev => new Set(prev).add(taskId));
      // Reload so the claimed task shows under "my to-dos" instead of just vanishing from the queue.
      onChanged?.();
    } catch {
      setClaimErrorId(taskId);
    }
  };

  return (
    <div className="mt-1 rounded-card p-3">
      <p className="mb-2 pl-0.5 text-micro font-semibold uppercase tracking-eyebrow text-ui-text3">
        my to-dos &middot; {personal.length}
      </p>

      {personal.map((r, idx) => {
        const itemId = sv(r['item_id']);
        const itemType = sv(r['item_type']);
        const ctxLabel = sv(r['context_label']);
        const ctx = ctxLabel
          ? { display_label: ctxLabel, context_type: sv(r['context_type']), entity_id: sv(r['context_entity_id']) }
          : null;

        if (itemType === 'task') {
          const task: TaskFull = {
            task_id: itemId,
            title: sv(r['title']) || 'Task',
            description: null,
            task_type: 'general',
            priority: sv(r['priority']) || 'normal',
            status: sv(r['status']),
            source: 'system',
            due_date: sv(r['due_on']) || null,
            context: ctx,
            owner_user_id: currentUserId,
            assignee_user_id: sv(r['assignee_user_id']) || null,
            queue_role_id: null,
            follower_user_ids: [],
            tags: [],
            deferred_until: null,
            active_reminder_count: r['has_reminder'] === true || String(r['has_reminder']) === 'true' ? 1 : 0,
            created_on: '', updated_on: '', created_by: null, is_following: false,
          };
          return (
            <div key={itemId || idx} className="mb-2">
              <TaskCard task={task} expandedId={expandedId} onExpand={setExpandedId} onDismissed={handleDismissed} />
            </div>
          );
        }

        const meeting: MeetingFull = {
          meeting_id: itemId,
          title: sv(r['title']) || 'Meeting',
          description: null,
          meeting_type: sv(r['meeting_type']) || 'other',
          status: sv(r['status']),
          start_time: sv(r['due_on']) || '',
          end_time: sv(r['end_time']) || '',
          location: null,
          context: ctx,
          participants: [],
          agenda: null,
          source_task_id: null,
          organizer_user_id: '',
          tags: [],
          created_on: '', updated_on: '',
        };
        return (
          <div key={itemId || idx} className="mb-2">
            <WorkspaceMeetingCard meeting={meeting} expandedId={expandedId} onExpand={setExpandedId} onDismissed={handleDismissed} />
          </div>
        );
      })}

      {personal.length === 0 && (
        <p className="mb-2 pl-0.5 text-xs text-ui-text2">No personal to-dos.</p>
      )}

      {queue.length > 0 && (
        <>
          <p className="mb-2 mt-3.5 pl-0.5 text-micro font-semibold uppercase tracking-eyebrow text-ui-text3">
            role queue &middot; {queue.length}
          </p>
          {queue.map((r, idx) => {
            const itemId = sv(r['item_id']);
            return (
              <QueueRow
                key={itemId || idx}
                r={r}
                onNavigate={() => onSelectTask(itemId)}
                onClaim={() => { void handleClaim(itemId); }}
                claiming={claiming}
                claimError={claimErrorId === itemId}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

// ─── WorkspaceTaskDetail ──────────────────────────────────────────────────────
// Fetches a task by id and renders TaskDetailPanel inside the workspace panel.

export function WorkspaceTaskDetail({ taskId, onBack, hideBackButton, activeTab, onTabChange, onActiveTask, onOpenEngagement }: {
  taskId: string;
  onBack: () => void;
  /** When true, the Back button is hidden (opened via ⋯ or agent, not from the task list). */
  hideBackButton?: boolean;
  activeTab?: TaskDetailTab;
  onTabChange?: (tab: TaskDetailTab) => void;
  /** Push the task's current fields to SAM (both agents) as active_task whenever the record
   *  changes — covers every manual edit (status, assign, follow, reminders, Save) since each one
   *  refetches it. Called with null on unmount (detail closed). */
  onActiveTask?: (task: Rec | null) => void;
  /** Navigate from a task's "Linked to: Engagement" to the engagement's property detail. */
  onOpenEngagement?: (engagementId: string) => void;
}) {
  const { data: task, isLoading, isError } = useGetTaskByIdQuery(taskId, WORKSPACE_QUERY_OPTIONS);

  // Resync-on-change: push whenever the backend record actually changes (any mutation refetches
  // it), not on every render — mirrors NoteDetailContent's lastSyncedOn pattern. The unmount-clear
  // effect uses a ref + empty deps so it fires ONLY on true unmount, not on every onActiveTask
  // identity change (e.g. a text/voice mode switch), which would otherwise wipe active_task and
  // then get skipped by the guard above (updated_on unchanged) instead of re-pushing it.
  const lastSyncedOn = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!task || task.updated_on === lastSyncedOn.current) return;
    lastSyncedOn.current = task.updated_on;
    onActiveTask?.(task as unknown as Rec);
  }, [task, onActiveTask]);
  const onActiveTaskRef = useRef(onActiveTask);
  onActiveTaskRef.current = onActiveTask;
  useEffect(() => () => onActiveTaskRef.current?.(null), []);

  return (
    <div className="flex flex-col h-full min-h-0">
      {!hideBackButton && (
        <button
          onClick={onBack}
          className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-accent-text hover:opacity-80 transition-opacity"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back to list
        </button>
      )}
      {isLoading && (
        <div className="flex justify-center py-12"><Spinner /></div>
      )}
      {isError && (
        <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-4">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-ui-text3">
            <rect x="3" y="3" width="18" height="18" rx="3"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="16" x2="12" y2="16"/>
          </svg>
          <p className="text-sm font-medium text-ui-text2">No tasks to show</p>
        </div>
      )}
      {task && (
        <div className="flex-1 overflow-y-auto no-scrollbar">
          <TaskDetailPanel task={task} activeTab={activeTab} onTabChange={onTabChange} onOpenEngagement={onOpenEngagement} />
        </div>
      )}
    </div>
  );
}

// ─── WorkspaceDirectEngagementDetail ─────────────────────────────────────────
// Fetches an engagement by id (to resolve property_id) then renders WorkspacePropertyDetail
// opened directly on that engagement. Used when the user navigates from a task's "Linked to" row.

function WorkspaceDirectEngagementDetail({ engagementId, onBack, onSelectTask, expandQuickQuoteEngagementId, openQuickQuoteMapEngagementId, quickQuoteScrollTick, expandTransitionEngagementId, expandTransitionCode, expandAssessmentCaptureEngagementId, expandAssessmentCaptureTick }: {
  engagementId: string;
  onBack: () => void;
  onSelectTask?: (id: string) => void;
  expandQuickQuoteEngagementId?: string | null;
  openQuickQuoteMapEngagementId?: string | null;
  quickQuoteScrollTick?: number;
  expandTransitionEngagementId?: string | null;
  expandTransitionCode?: string | null;
  expandAssessmentCaptureEngagementId?: string | null;
  expandAssessmentCaptureTick?: number;
}) {
  const { data: engagement, isLoading, isError } = useGetEngagementByIdQuery(engagementId);
  if (isLoading) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <button onClick={onBack} className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-accent-text hover:opacity-80 transition-opacity">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to task
        </button>
        <div className="flex justify-center py-12"><Spinner /></div>
      </div>
    );
  }
  if (isError || !engagement?.property_id) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <button onClick={onBack} className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-accent-text hover:opacity-80 transition-opacity">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to task
        </button>
        <p className="text-center text-danger-text py-8 text-sm">Could not load engagement.</p>
      </div>
    );
  }
  return (
    <WorkspacePropertyDetail
      propertyId={engagement.property_id}
      initialEngagementId={engagementId}
      onBack={onBack}
      onSelectTask={onSelectTask}
      expandQuickQuoteEngagementId={expandQuickQuoteEngagementId}
      openQuickQuoteMapEngagementId={openQuickQuoteMapEngagementId}
      quickQuoteScrollTick={quickQuoteScrollTick}
      expandTransitionEngagementId={expandTransitionEngagementId}
      expandTransitionCode={expandTransitionCode}
      expandAssessmentCaptureEngagementId={expandAssessmentCaptureEngagementId}
      expandAssessmentCaptureTick={expandAssessmentCaptureTick}
    />
  );
}

// ─── WorkspaceNoteDetail ──────────────────────────────────────────────────────
// Renders the full note review card (summary / transcript / follow-ups, inline edit) inside the
// workspace panel — reuses NoteDetailContent so the SAM-driven flow and the /notes page stay one UI.

function WorkspaceNoteDetail({ noteId, onBack, onOpenTask, onNoteSynced, onNotesListChanged }: {
  noteId: string;
  onBack: () => void;
  onOpenTask?: (taskId: string) => void;
  /** Push the note's current fields to SAM (both agents) as active_note whenever the record
   *  changes or the user edits it on screen; null clears it (detail closed). */
  onNoteSynced?: (note: Rec | null) => void;
  /** A note was deleted from this detail view — refresh the on-screen notes list card (a
   *  separate cache from notesApi's own 'Note' tag) so the deleted note doesn't linger in the
   *  list underneath once "Back to list" is clicked. */
  onNotesListChanged?: () => void;
}) {
  const [deleteNote] = useDeleteNoteMutation();
  const handleDelete = async () => {
    await deleteNote(noteId).unwrap().catch(() => undefined);
    onNotesListChanged?.();
    onBack();
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <button
        onClick={onBack}
        className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-accent-text hover:opacity-80 transition-opacity"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Back to list
      </button>
      <div className="flex-1 min-h-0">
        <NoteDetailContent noteId={noteId} onClose={onBack} onOpenTask={onOpenTask} onDelete={handleDelete} onNoteSynced={onNoteSynced} />
      </div>
    </div>
  );
}

// ─── WorkspaceMeetingDetail ───────────────────────────────────────────────────
// Fetches a meeting by id and renders MeetingDetailPanel inside the workspace panel.

export function WorkspaceMeetingDetail({ meetingId, onBack, hideBackButton, activeTab, onActiveMeeting, onStartNoteTaker }: {
  meetingId: string;
  onBack: () => void;
  /** When true, the Back button is hidden (opened via ⋯ or agent, not from the meeting list). */
  hideBackButton?: boolean;
  activeTab?: 'details' | 'notes' | 'activity';
  /** Push the meeting's current fields to SAM (both agents) as active_meeting whenever the
   *  record changes — covers reschedule/RSVP/complete/cancel/participants (each refetches it).
   *  Called with null on unmount (detail closed). */
  onActiveMeeting?: (meeting: Rec | null) => void;
  /** Open the Note Taker with this meeting pre-selected as context. */
  onStartNoteTaker?: (contextType: string, entityId: string) => void;
}) {
  const { data: meeting, isLoading, isError } = useGetMeetingByIdQuery(meetingId, WORKSPACE_QUERY_OPTIONS);

  // Resync-on-change — see WorkspaceTaskDetail for the identical pattern + the reasoning behind
  // the ref-based unmount-only cleanup.
  const lastSyncedOn = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!meeting || meeting.updated_on === lastSyncedOn.current) return;
    lastSyncedOn.current = meeting.updated_on;
    onActiveMeeting?.(meeting as unknown as Rec);
  }, [meeting, onActiveMeeting]);
  const onActiveMeetingRef = useRef(onActiveMeeting);
  onActiveMeetingRef.current = onActiveMeeting;
  useEffect(() => () => onActiveMeetingRef.current?.(null), []);

  return (
    <div className="flex flex-col h-full min-h-0">
      {!hideBackButton && (
        <button
          onClick={onBack}
          className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-accent-text hover:opacity-80 transition-opacity"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back to list
        </button>
      )}
      {isLoading && (
        <div className="flex justify-center py-12"><Spinner /></div>
      )}
      {isError && (
        <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-4">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ui-text3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <p className="text-sm font-medium text-ui-text2">Nothing to show</p>
        </div>
      )}
      {meeting && (
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {onStartNoteTaker && (
            <div className="px-4 pt-3 pb-1">
              <button
                type="button"
                onClick={() => onStartNoteTaker('meeting', meetingId)}
                className="flex items-center gap-2 w-full rounded-control border border-accent/40 bg-accent/5 hover:bg-accent/10 px-4 py-2.5 text-sm font-medium text-accent-text transition"
              >
                <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 flex-shrink-0" aria-hidden>
                  <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z" fill="currentColor" />
                  <path d="M19 11a7 7 0 0 1-14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Start Note Taker
              </button>
            </div>
          )}
          <MeetingDetailPanel meeting={meeting} activeTab={activeTab} />
        </div>
      )}
    </div>
  );
}

// ─── WorkspacePropertyDetail ──────────────────────────────────────────────────
// Fetches a property's aggregated detail (contacts, engagements, tasks, timeline) and renders it
// inside the workspace panel — the Property CRM anchor view (CRM-FEAT-01/02/03, MVP slice).

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

function ContactRow({ contact, compact, extraPhoneCount, onExtraClick }: {
  contact: PropertyContact;
  compact?: boolean;
  extraPhoneCount?: number;
  /** Called when the user taps the "+N" overflow badge — e.g. to navigate to Details. */
  onExtraClick?: () => void;
}) {
  return (
    <div className={`flex items-center rounded-control border border-ui-border bg-ui-subtle ${compact ? 'gap-2 px-2.5 py-1.5' : 'gap-3 px-3 py-2.5'}`}>
      <div className={`flex-shrink-0 rounded-full bg-accent/15 text-accent-text font-semibold flex items-center justify-center ${compact ? 'h-6 w-6 text-xs' : 'h-9 w-9 text-xs'}`}>
        {initials(contact.name)}
      </div>
      <div className="min-w-0 flex-1 flex items-center gap-1.5 flex-wrap">
        <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-ui-text`}>{contact.name}</span>
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            title="Call"
            className="flex-shrink-0 h-5 w-5 rounded-full bg-ui-hover hover:bg-accent/10 flex items-center justify-center text-ui-text2 hover:text-accent-text transition-colors"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          </a>
        )}
        {contact.phone && (
          <>
            <span className="text-ui-text3 text-xs">·</span>
            <a href={`tel:${contact.phone}`} className="text-xs text-ui-text3 hover:text-accent-text transition-colors">{contact.phone}</a>
          </>
        )}
        {contact.role && !compact && (
          <span className="flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-ui-hover text-ui-text2 whitespace-nowrap">
            {humanizeLabel(contact.role)}
          </span>
        )}
        {extraPhoneCount && extraPhoneCount > 0 ? (
          onExtraClick ? (
            <button
              type="button"
              onClick={onExtraClick}
              className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-ui-hover text-ui-text2 hover:bg-ui-border hover:text-ui-text transition-colors"
              title="View all contacts in Details"
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              +{extraPhoneCount}
            </button>
          ) : (
            <span className="flex-shrink-0 text-xs font-semibold px-2 py-1 rounded-full bg-ui-hover text-ui-text3">+{extraPhoneCount}</span>
          )
        ) : null}
      </div>
    </div>
  );
}

// ─── PropertyPhotosSection — real photo upload flow + gallery ─────────────
const MEDIA_CLASSIFICATIONS = [
  // view
  'front', 'back', 'left', 'right', 'aerial', 'interior',
  // area
  'roof', 'gutters', 'flashing', 'chimney', 'vents', 'skylight', 'attic', 'yard',
  // stage
  'before', 'during', 'after', 'damage', 'warranty_evidence',
];

function PropertyPhotosSection({ propertyId, engagementId, media: allMedia, readOnly }: {
  propertyId: string;
  engagementId?: string;
  media: Array<{ media_id: string; media_type: string; s3_url: string; filename?: string | null; classifications: string[]; caption?: string | null }>;
  /** Media tab renders read-only (aggregated view across all engagements + property); uploads
   *  only happen from inside an opened engagement, tagged with that engagement_id. */
  readOnly?: boolean;
}) {
  // The same /properties/{id}/media list now carries photo, video AND file records; the
  // Files section renders the non-visual ones separately, so gate the gallery here.
  const media = allMedia.filter(m => m.media_type === 'photo' || m.media_type === 'video');
  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [classifications, setClassifications] = useState<string[]>([]);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, { isLoading: uploading }] = useUploadMediaFileMutation();
  const [attach, { isLoading: attaching }] = useAttachPropertyMediaMutation();

  const canSubmit = !!file && classifications.length > 0 && !uploading && !attaching;

  async function submit() {
    if (!file) return;
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const uploaded = await uploadFile(fd).unwrap();
      await attach({
        propertyId,
        body: {
          engagement_id: engagementId,
          s3_url: uploaded.url,
          media_type: file.type.startsWith('video/') ? 'video' : 'photo',
          filename: uploaded.filename,
          classifications,
          caption: caption.trim() || undefined,
        },
      }).unwrap();
      setModalOpen(false);
      setFile(null);
      setClassifications([]);
      setCaption('');
    } catch (e) {
      setError('Upload failed — please try again.');
    }
  }

  return (
    <>
      <CardSection
        title="Photos"
        count={media.length}
        onAdd={readOnly ? undefined : () => setModalOpen(true)}
        addLabel="Add Photo"
        emptyText={
          media.length === 0
            ? readOnly
              ? "No photos yet. Add them from inside an engagement."
              : "No photos yet. Add before, during, and after shots — future crews will thank you."
            : undefined
        }
      >
        {media.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-2">
              {media.slice(0, 6).map(m => (
                <a
                  key={m.media_id}
                  href={m.s3_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group aspect-square rounded-chip overflow-hidden border border-ui-border bg-ui-subtle relative flex items-center justify-center"
                  title={m.caption || m.filename || ''}
                >
                  {m.media_type === 'video' ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-ui-text3"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                  ) : (
                    <img src={m.s3_url} alt={m.caption || m.filename || ''} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  )}
                  {m.classifications.length > 0 && (
                    <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-1">
                      {m.classifications.slice(0, 2).map(cls => (
                        <span key={cls} className="text-xs font-medium px-1.5 py-0.5 rounded bg-black/60 text-white truncate max-w-full">
                          {humanizeLabel(cls)}
                        </span>
                      ))}
                    </div>
                  )}
                </a>
              ))}
            </div>
            {media.length > 6 && <p className="text-xs text-ui-text3">+{media.length - 6} more</p>}
          </>
        )}
      </CardSection>

      {modalOpen && (
        <Modal
          title="Add a photo"
          onClose={() => setModalOpen(false)}
          maxWidthClassName="max-w-md"
          footer={
            <>
              <Button size="sm" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button size="sm" disabled={!canSubmit} onClick={submit}>
                {uploading || attaching ? 'Uploading…' : 'Upload photo'}
              </Button>
            </>
          }
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ui-text2">Photo or short video</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/mp4,video/quicktime,video/webm"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="text-xs file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-accent/10 file:text-accent-text file:font-medium hover:file:bg-accent/15"
            />
            {file && <span className="text-xs text-ui-text3">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span>}
          </label>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ui-text2">Tag it (pick one or more)</span>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto no-scrollbar">
              {MEDIA_CLASSIFICATIONS.map(cls => {
                const active = classifications.includes(cls);
                return (
                  <button
                    key={cls}
                    type="button"
                    onClick={() => setClassifications(prev => active ? prev.filter(c => c !== cls) : [...prev, cls])}
                    className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                      active
                        ? 'bg-accent-fill text-on-accent border-accent'
                        : 'bg-ui-subtle text-ui-text2 border-ui-border hover:bg-ui-hover'
                    }`}
                  >
                    {humanizeLabel(cls)}
                  </button>
                );
              })}
            </div>
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ui-text2">Caption (optional)</span>
            <input
              type="text"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="e.g. Impact damage on north slope after 2026-06 storm"
              className={fieldClass()}
            />
          </label>
          {error && <p className="text-xs text-danger-text">{error}</p>}
        </Modal>
      )}
    </>
  );
}

// ─── PropertyFilesSection — non-image/video attachments (PDFs, docs, spreadsheets) ─────
const FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,application/zip';

function fileIconFor(name?: string | null): string {
  const ext = (name || '').split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf') return '📕';
  if (['doc', 'docx'].includes(ext)) return '📄';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['ppt', 'pptx'].includes(ext)) return '📽️';
  if (ext === 'zip') return '🗜️';
  return '📎';
}

function PropertyFilesSection({ propertyId, engagementId, media: allMedia, readOnly }: {
  propertyId: string;
  engagementId?: string;
  media: Array<{ media_id: string; media_type: string; s3_url: string; filename?: string | null; classifications: string[]; caption?: string | null }>;
  readOnly?: boolean;
}) {
  const files = allMedia.filter(m => m.media_type === 'file');
  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [uploadFile, { isLoading: uploading }] = useUploadMediaFileMutation();
  const [attach, { isLoading: attaching }] = useAttachPropertyMediaMutation();

  async function submit() {
    if (!file) return;
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const uploaded = await uploadFile(fd).unwrap();
      await attach({
        propertyId,
        body: {
          engagement_id: engagementId,
          s3_url: uploaded.url,
          media_type: 'file',
          filename: uploaded.filename,
          caption: caption.trim() || undefined,
        },
      }).unwrap();
      setModalOpen(false);
      setFile(null);
      setCaption('');
    } catch {
      setError('Upload failed — please try again.');
    }
  }

  return (
    <>
      <CardSection
        title="Files"
        count={files.length}
        onAdd={readOnly ? undefined : () => setModalOpen(true)}
        addLabel="Add File"
        emptyText={files.length === 0
          ? readOnly ? "No files yet. Add them from inside an engagement."
                     : "No files yet. Drop in a signed contract, permit, or inspection report."
          : undefined}
      >
        {files.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {files.map(f => (
              <a
                key={f.media_id}
                href={f.s3_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 rounded-chip border border-ui-border bg-ui-subtle px-3 py-2 hover:bg-ui-hover transition-colors"
              >
                <span className="text-lg flex-shrink-0" aria-hidden="true">{fileIconFor(f.filename)}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ui-text truncate">{f.filename || 'File'}</p>
                  {f.caption && <p className="text-xs text-ui-text3 truncate">{f.caption}</p>}
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 text-ui-text3">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </a>
            ))}
          </div>
        )}
      </CardSection>

      {modalOpen && (
        <Modal
          title="Add a file"
          onClose={() => setModalOpen(false)}
          maxWidthClassName="max-w-md"
          footer={
            <>
              <Button size="sm" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button size="sm" disabled={!file || uploading || attaching} onClick={submit}>
                {uploading || attaching ? 'Uploading…' : 'Upload file'}
              </Button>
            </>
          }
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ui-text2">Document</span>
            <input
              type="file"
              accept={FILE_ACCEPT}
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="text-xs file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-accent/10 file:text-accent-text file:font-medium hover:file:bg-accent/15"
            />
            {file && <span className="text-xs text-ui-text3">{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB</span>}
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ui-text2">Caption (optional)</span>
            <input
              type="text"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="e.g. Signed material approval, GAF Timberline HDZ"
              className={fieldClass()}
            />
          </label>
          {error && <p className="text-xs text-danger-text">{error}</p>}
        </Modal>
      )}
    </>
  );
}

// PropertyNotesSection moved to @/components/Properties/PropertyNotesSection.tsx — WorkflowCard
// needed it too (for AssessmentCaptureCard), and this file already imports WorkflowCard, so it
// couldn't stay defined here without a circular import.

// ─── EngagementActivitySection ───────────────────────────────────
// Base config — dot colour may be overridden at render time based on detail.status
const ACTIVITY_CFG: Record<string, { label: string; dot: string }> = {
  engagement_created:          { label: 'Engagement created',       dot: 'bg-accent' },
  stage_advanced:              { label: 'Stage advanced',            dot: 'bg-success' },
  status_changed:              { label: 'Status moved',              dot: 'bg-accent' },
  transition_executed:         { label: 'Action taken',             dot: 'bg-success' },
  hook_executed:               { label: 'Automation ran',           dot: 'bg-success' },   // overridden if status=failed
  governance_action_executed:  { label: 'Gate action executed',     dot: 'bg-success' },   // overridden if status=failed
  note_added:                  { label: 'Note added',               dot: 'bg-accent' },
  call_logged:                 { label: 'Call logged',              dot: 'bg-accent' },
  assessment_completed:        { label: 'Assessment completed',     dot: 'bg-success' },
  put_on_hold:                 { label: 'Put on hold',              dot: 'bg-warning' },
  resumed:                     { label: 'Resumed',                  dot: 'bg-success' },
  closed_early:                { label: 'Closed early',             dot: 'bg-danger' },
  holder_reassigned:           { label: 'Holder reassigned',        dot: 'bg-accent' },
  access_policy_updated:       { label: 'Access policy updated',    dot: 'bg-ui-text3' },
  task_created:                { label: 'Task created',             dot: 'bg-accent' },
  task_completed:              { label: 'Task completed',           dot: 'bg-success' },
  approval_requested:          { label: 'Approval requested',       dot: 'bg-warning' },
  approval_decided:            { label: 'Approval decided',         dot: 'bg-success' },
};

// Maps backend hook_code / action_type to a human-friendly label.
function hookLabel(hookCode: string | null | undefined, actionType: string | null | undefined): string {
  if (actionType === 'task_creation') return 'Task auto-created';
  if (actionType === 'approval_creation') return 'Approval auto-created';
  if (actionType === 'notification') return 'Notification sent';
  if (actionType === 'module_action') return 'Module action triggered';
  if (hookCode) return humanizeLabel(hookCode.replace(/_/g, ' '));
  if (actionType) return humanizeLabel(String(actionType).replace(/_/g, ' '));
  return 'Automation ran';
}

function activityDetail(a: EngagementActivity): string | null {
  const d = a.detail;
  if (!d) return null;
  const s = (v: unknown) => String(v);
  switch (a.activity_type) {
    case 'stage_advanced':
      if (d.from_stage && d.to_stage) return `${s(d.from_stage)} → ${s(d.to_stage)}`;
      if (d.to_stage) return `Moved to ${s(d.to_stage)}`;
      break;
    case 'status_changed':
      if (d.old_status && d.new_status) return `${humanizeLabel(s(d.old_status))} → ${humanizeLabel(s(d.new_status))}`;
      if (d.from_status && d.to_status) return `${humanizeLabel(s(d.from_status))} → ${humanizeLabel(s(d.to_status))}`;
      break;
    case 'engagement_created': {
      const displayLabels = d.display_labels as Record<string, unknown> | undefined;
      if (displayLabels?.status) return `Started at ${s(displayLabels.status)}`;
      if (d.initial_status) return `Started at ${humanizeLabel(s(d.initial_status))}`;
      break;
    }
    case 'transition_executed': {
      const actionLabel = d.action_label ? s(d.action_label) : (d.transition_code ? humanizeLabel(s(d.transition_code)) : null);
      const fromLabel = d.from_status_label ? s(d.from_status_label) : null;
      const toLabel = d.to_status_label ? s(d.to_status_label) : null;
      if (fromLabel && toLabel) return `${actionLabel ? actionLabel + ' · ' : ''}${fromLabel} → ${toLabel}`;
      if (actionLabel) return actionLabel;
      break;
    }
    case 'hook_executed': {
      const hookCode = d.hook_code as string | undefined;
      const actionType = d.action_type as string | undefined;
      const triggerRef = d.trigger_ref as string | undefined;
      const status = d.status as string | undefined;
      if (status === 'failed' && d.error_detail) return `Failed: ${s(d.error_detail)}`;
      const base = hookLabel(hookCode, actionType);
      // Append trigger context for clarity, e.g. "Task auto-created on entering New Prospect"
      if (triggerRef) return `${base} · ${humanizeLabel(s(triggerRef))}`;
      return base;
    }
    case 'governance_action_executed': {
      const gate = d.gate_code as string | undefined;
      const action = d.action_key as string | undefined;
      const status = d.status as string | undefined;
      if (status === 'failed' && d.error_detail) return `Failed: ${s(d.error_detail)}`;
      if (gate && action) return `${humanizeLabel(s(gate))} — ${humanizeLabel(s(action))}`;
      if (gate) return humanizeLabel(s(gate));
      break;
    }
    case 'put_on_hold':
      if (d.reason) return s(d.reason);
      break;
    case 'closed_early':
      if (d.reason) return s(d.reason);
      if (d.closure_type) return humanizeLabel(s(d.closure_type));
      break;
    case 'holder_reassigned':
      if (d.new_holder_name) return `Reassigned to ${s(d.new_holder_name)}`;
      break;
    case 'approval_decided':
      if (d.decision && d.comments) return `${humanizeLabel(s(d.decision))} — ${s(d.comments)}`;
      if (d.decision) return humanizeLabel(s(d.decision));
      break;
  }
  if (d.description) return s(d.description);
  return null;
}

// Dynamic dot colour: success/failure hooks override the base config colour.
function activityDotColor(a: EngagementActivity, baseDot: string): string {
  if (a.activity_type === 'hook_executed' || a.activity_type === 'governance_action_executed') {
    const status = a.detail?.status as string | undefined;
    if (status === 'success') return 'bg-success';
    if (status === 'failed') return 'bg-danger';
  }
  return baseDot;
}

function relTimeShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

const ACTIVITY_PREVIEW = 5;

/** Every meeting linked to this engagement (site visits, client meetings, follow-ups — not just
 *  the assessment WorkflowCard already shows), newest first. */
function EngagementMeetingsSection({ engagementId }: { engagementId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useGetMeetingsListQuery(
    { context_type: 'engagement', context_entity_id: engagementId, sort_by: 'start_time', sort_dir: -1, limit: 20 },
    { refetchOnMountOrArgChange: true },
  );
  // The assessment meeting already renders inside WorkflowCard's assessment card — don't list it twice.
  const allMeetings = data?.data ?? [];
  const meetings = allMeetings.filter(m => m.meeting_type !== 'assessment');
  // The assessment visit is hidden here because the workflow card shows it — say so, or "no meetings"
  // reads as if the booking failed (D1).
  const hidAssessment = allMeetings.length > meetings.length;
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  return (
    <CardSection
      title={t('engagement.meetings.title')}
      count={meetings.length}
      emptyText={!isLoading && meetings.length === 0 ? t(hidAssessment ? 'engagement.meetings.noOtherMeetings' : 'engagement.meetings.empty') : undefined}
    >
      {isLoading ? (
        <div className="flex justify-center py-2"><Spinner size="sm" /></div>
      ) : (
        <div className="flex flex-col gap-2">
          {meetings.map(m => (
            <WorkspaceMeetingCard key={m.meeting_id} meeting={m} expandedId={expandedId} onExpand={setExpandedId} />
          ))}
        </div>
      )}
    </CardSection>
  );
}

function EngagementActivitySection({ engagementId }: { engagementId: string }) {
  // workflow_engine.engagement_activity_log_enabled: the section shows only when the setting is
  // true. Until it has loaded (or if it fails to) nothing is shown and nothing is fetched.
  const { data: workflowCfg } = useGetConfigurationQuery('workflow_engine');
  const enabled = workflowCfg?.data?.effective_value?.engagement_activity_log_enabled === true;
  // refetchOnMountOrArgChange: true — always fetch fresh data when this panel is opened/navigated to.
  const { data: items = [], isLoading } = useGetEngagementActivityQuery(engagementId, { refetchOnMountOrArgChange: true, skip: !enabled });
  const [showAll, setShowAll] = React.useState(false);
  // Sort descending by occurred_at so the latest activity is always at the top.
  const sorted = [...items].sort(
    (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
  );
  const visible = showAll ? sorted : sorted.slice(0, ACTIVITY_PREVIEW);
  const hasMore = sorted.length > ACTIVITY_PREVIEW;

  if (!enabled) return null;
  return (
    <CardSection
      title="Activity"
      count={items.length}
      emptyText={!isLoading && items.length === 0 ? 'No activity recorded yet.' : undefined}
    >
      {isLoading ? (
        <div className="flex justify-center py-2"><Spinner size="sm" /></div>
      ) : sorted.length > 0 ? (
        <div className="relative">
          {/* Vertical connector line */}
          <div className="absolute left-[5px] top-2 bottom-2 w-px bg-ui-border" />
          <div className="flex flex-col gap-3">
            {visible.map((a, i) => {
              const cfg = ACTIVITY_CFG[a.activity_type];
              // For hook/automation events use the richer hookLabel as the main entry title.
              const label = (a.activity_type === 'hook_executed')
                ? hookLabel(a.detail?.hook_code as string | undefined, a.detail?.action_type as string | undefined)
                : (cfg?.label ?? humanizeLabel(a.activity_type));
              const dot = activityDotColor(a, cfg?.dot ?? 'bg-ui-text3');
              const desc = activityDetail(a);
              // actor_name is now populated for hook events after the backend fix;
              // fall back to "System" for automated/internal events.
              const actor = a.actor_name ?? 'System';
              const failed = (a.activity_type === 'hook_executed' || a.activity_type === 'governance_action_executed')
                && (a.detail?.status as string | undefined) === 'failed';
              return (
                <div key={i} className="flex items-start gap-3 relative">
                  <span className={`mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ${dot} ring-2 ring-ui-card z-10`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`text-xs font-medium ${failed ? 'text-danger-text' : 'text-ui-text'}`}>{label}</span>
                      <span className="flex-shrink-0 text-xs text-ui-text3">{relTimeShort(a.occurred_at)}</span>
                    </div>
                    {desc && (
                      <p className="mt-0.5 text-xs text-ui-text2 leading-snug">{desc}</p>
                    )}
                    <p className="mt-0.5 text-xs text-ui-text3">{actor}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {hasMore && (
            <button
              onClick={() => setShowAll(v => !v)}
              className="mt-3 ml-[18px] text-xs text-brand font-medium hover:underline"
            >
              {showAll ? 'Show less' : `View ${sorted.length - ACTIVITY_PREVIEW} more`}
            </button>
          )}
        </div>
      ) : null}
    </CardSection>
  );
}

// ─── PropertyMeasurementsSection — inline mini-form ─────────────
// PropertyMeasurementsSection moved to @/components/Properties/PropertyMeasurementsSection.tsx —
// same reason as PropertyNotesSection above (WorkflowCard needs it, can't be defined here).

export function WorkspacePropertyDetail({ propertyId, onBack, onSelectTask, initialEngagementId, hideBackButton, engagementRev, expandQuickQuoteEngagementId, openQuickQuoteMapEngagementId, quickQuoteScrollTick, expandTransitionEngagementId, expandTransitionCode, expandAssessmentCaptureEngagementId, expandAssessmentCaptureTick, proposalPreparedViewRequest, proposalSurfaceInteractRequest }: {
  propertyId: string;
  onBack: () => void;
  /** Opens a task detail from the engagement's Tasks section (workflow-created tasks). */
  onSelectTask?: (id: string) => void;
  /** Reached from the Engagements list ("open the first one") — go straight to that
   * engagement's WorkflowCard instead of the tab's own multi-engagement picker list. */
  initialEngagementId?: string | null;
  /** No "list" to return to when this card is the top-level surface (e.g. reached directly
   *  from a SAM workflow tool call rather than from the Properties/Engagements list). */
  hideBackButton?: boolean;
  /** Bumped whenever SAM re-opens/re-points this surface at initialEngagementId — forces the
   *  embedded WorkflowCard to refetch even when nothing invalidated its RTK Query cache. */
  engagementRev?: number;
  /** The engagement_id SAM requested to expand the Quick Quote section for — passed down so
   *  the embedded WorkflowCard can auto-expand its inline Quick Quote when ids match. */
  expandQuickQuoteEngagementId?: string | null;
  /** The engagement_id SAM requested to open the satellite map for — passed down so
   *  the embedded WorkflowCard can auto-open the map when ids match. */
  openQuickQuoteMapEngagementId?: string | null;
  /** See useChatWorkspace — incremented on REFRESH_QUICK_QUOTE to trigger scroll. */
  quickQuoteScrollTick?: number;
  /** The engagement_id SAM requested to open a blocked transition's business_capture form for —
   *  passed down so the embedded WorkflowCard can auto-open the right transition's panel. */
  expandTransitionEngagementId?: string | null;
  /** The transition_code to open — paired with expandTransitionEngagementId. */
  expandTransitionCode?: string | null;
  expandAssessmentCaptureEngagementId?: string | null;
  expandAssessmentCaptureTick?: number;
  /** Last proposal_prepared_open request from SAM — open the named panel on the Proposal Prepared card.
   *  Scoped by engagementId; only applies when it matches the currently-open engagement. */
  proposalPreparedViewRequest?: { engagementId: string; view: 'preview_proposal' | 'view_estimate' | 'open_assessment'; tick: number } | null;
  proposalSurfaceInteractRequest?: ProposalSurfaceInteractPayload & { tick: number } | null;
}) {
  const { data: property, isLoading, isError } = useGetPropertyDetailQuery(propertyId, WORKSPACE_QUERY_OPTIONS);
  // The address section (contacts, warranties, measurements, media) is part of this card whenever it is open.
  useSamSectionContext('property', { property_id: propertyId });
  // Records an explicit engagement switch (header dropdown, or the picker list below) into the
  // "⋯" recent-views menu — same shape/context StartEngagementInline's own onUserView call uses
  // for a freshly-created engagement, so a manually-opened existing one collapses into the same
  // kind of entry rather than being invisible to that menu.
  const onUserView = useContext(UserViewContext);
  // Where an explicit engagement pick (header dropdown, card-picker list) reports itself so the
  // choice survives this component remounting on the mobile chat<->workspace toggle — see
  // PendingEngagementContext's declaration.
  const pendingEngagementCtx = useContext(PendingEngagementContext);
  // PCR-FEAT-03 Property Profile sub-lists — parallel fetches so the detail streams in as
  // pieces arrive. Each has its own RTK Query cache tag so SAM tool actions targeted at a
  // specific section (attach_property_media, create_warranty, create_measurement,
  // create_property_note) invalidate just that section.
  const { data: media = [] } = useGetPropertyMediaQuery(propertyId, WORKSPACE_QUERY_OPTIONS);
  const { data: measurements = [] } = useGetPropertyMeasurementsQuery(propertyId, WORKSPACE_QUERY_OPTIONS);
  const { data: propNotes = [] } = useGetPropertyNotesQuery(propertyId, WORKSPACE_QUERY_OPTIONS);
  const { data: propertyContacts = [] } = useGetPropertyContactsQuery(propertyId, WORKSPACE_QUERY_OPTIONS);
  // Default to Engagement — this card is reached via the property/engagements list, where the
  // engagement (its stage/status) is what the user just came in to look at; Media starts empty
  // until something's uploaded, so opening there first is a wasted extra tap.
  const [tab, setTab] = useState<'media' | 'engagement'>('engagement');
  // When there are multiple engagements, the tab shows the minimal card list until one is
  // The default state is always the engagement list; the detail view opens only on an explicit
  // user action (click row, SAM tool, or "+" create). initialEngagementId seeds the state so
  // arriving from a list click or SAM opens that specific engagement directly.
  const [openEngagementId, setOpenEngagementId] = useState<string | null>(initialEngagementId ?? null);
  // Set to true when the user explicitly clicks "Back to engagements" so the useEffect below
  // doesn't immediately re-apply initialEngagementId and trap them in a loop. Reset whenever
  // initialEngagementId itself changes (SAM re-pointing to a different engagement).
  const userBackedRef = useRef(false);
  useEffect(() => { userBackedRef.current = false; }, [initialEngagementId]);
  // Holds the ID of a newly created engagement until the list re-fetches and confirms it
  const pendingNewEngagementRef = useRef<string | null>(null);
  const [engagementDropdownOpen, setEngagementDropdownOpen] = useState(false);
  const engagementDropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastStatusCodeRef = useRef<string | undefined>(undefined);
  const [stageRailVisible, setStageRailVisible] = useState(true);
  const { data: openWorkflowState } = useGetEngagementWorkflowStateQuery(openEngagementId ?? '', { skip: !openEngagementId });
  // Scroll the panel back to top when the open engagement's status changes (a transition moved it
  // forward). Derived directly from this component's own workflow-state query rather than a
  // callback WorkflowCard used to forward up as part of its (now removed) SAM-reporting chain.
  useEffect(() => {
    const newCode = openWorkflowState?.current_status_code;
    if (!newCode) return;
    const prevCode = lastStatusCodeRef.current;
    lastStatusCodeRef.current = newCode;
    if (prevCode !== undefined && newCode !== prevCode) {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [openWorkflowState?.current_status_code]);
  useEffect(() => {
    if (!engagementDropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (engagementDropdownRef.current && !engagementDropdownRef.current.contains(e.target as Node)) {
        setEngagementDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [engagementDropdownOpen]);
  const [propertyMenuOpen, setPropertyMenuOpen] = useState(false);
  const propertyMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!propertyMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (propertyMenuRef.current && !propertyMenuRef.current.contains(e.target as Node)) {
        setPropertyMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [propertyMenuOpen]);
  const [propertyInfoOpen, setPropertyInfoOpen] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const { data: menuEngagement } = useGetEngagementByIdQuery(
    openEngagementId ?? '',
    { skip: (!propertyMenuOpen && !propertyInfoOpen && !teamModalOpen) || !openEngagementId, ...WORKSPACE_QUERY_OPTIONS },
  );
  const [_menuSection, setMenuSection] = useState<'contacts' | 'notes' | 'measurements' | 'photos' | 'files' | null>(null);
  useEffect(() => {
    if (!propertyMenuOpen) setPreviewMediaUrl(null);
    if (!propertyInfoOpen) setMenuSection(null);
  }, [propertyMenuOpen, propertyInfoOpen]);
  const [previewMediaUrl, setPreviewMediaUrl] = useState<string | null>(null);
  const [addressExpanded, setAddressExpanded] = useState(false);

  // Prefer the Contacts-module primary POC (fresh, comes from property_contacts via
  // primary_contact) over the legacy embedded `contacts[]` array — same precedence as
  // WorkspacePropertyCard's owner display, and for the same reason: contacts added through
  // link_contact_to_property never land in `contacts[]`, so relying on that array alone
  // silently misses any contact added the new way.
  const primaryContactSummary = property?.primary_contact;
  const primary: PropertyContact | undefined = primaryContactSummary
    ? {
        name: primaryContactSummary.display_name,
        role: primaryContactSummary.relationship_type || 'owner',
        is_primary: true,
        phone: primaryContactSummary.phone,
        email: primaryContactSummary.email,
      }
    : property?.contacts.find(c => c.is_primary) || property?.contacts[0];

  // Header shows only the primary POC — contact_summary.primary_poc is the freshest source
  // (refreshed by the contacts-module worker whenever the contact list changes); extras are
  // shown as "+N" so the header stays compact. Full contact list is accessible via Details tab.
  const pocSummary = property?.contact_summary?.primary_poc;
  const headerPoc: PropertyContact | undefined = pocSummary
    ? {
        name: pocSummary.display_name,
        // Use the real relationship type from primary_contact; suppress 'primary_poc' label.
        role: property?.primary_contact?.relationship_type || '',
        is_primary: true,
        phone: pocSummary.phone ?? null,
        email: pocSummary.email ?? null,
      }
    : primary;
  // Fall back to the first linked contact when there is no designated primary POC or owner
  // (e.g. a newly linked engagement_contact before a proper owner/poc is set).
  const firstLinkedContact = propertyContacts.find(c => c.is_current && c.contact);
  const displayPoc: PropertyContact | undefined = headerPoc ?? (firstLinkedContact?.contact
    ? {
        name: firstLinkedContact.contact.display_name,
        role: firstLinkedContact.relationship_type.replace(/_/g, ' '),
        is_primary: false,
        phone: firstLinkedContact.contact.phones[0]?.value ?? null,
        email: firstLinkedContact.contact.emails[0]?.value ?? null,
      }
    : undefined);
  const totalContactCount = property?.contact_summary?.total_contacts ?? propertyContacts.length;
  const extraContactCount = Math.max(0, totalContactCount - 1);

  const engagements = property?.engagements ?? [];
  // Auto-open the only engagement so the tab lands on the card, not a one-item list; also seeds/
  // re-syncs openEngagementId from initialEngagementId — needed because (a) this component
  // doesn't remount when SAM re-points an already-open surface at a different engagement/property
  // (propertyId and initialEngagementId are just props, not keys), and (b) when propertyId itself
  // just changed, `engagements` is briefly [] while this property's own query is in flight — must
  // wait rather than clear, or this effect's own "clear stale selection" branch below would wipe
  // out the freshly-requested target before its property's data ever arrives to confirm it.
  useEffect(() => {
    if (tab !== 'engagement') return;
    // If the user just created a new engagement via "+", select it once the list contains it —
    // checked before initialEngagementId below, since clicking "+" is a more recent, explicit
    // user action than whatever engagement SAM last pointed this surface at. Otherwise the
    // initialEngagementId branch keeps winning (it's still valid/present) and the freshly
    // created engagement never gets selected even though it was created successfully.
    if (pendingNewEngagementRef.current) {
      if (engagements.some(e => e.engagement_id === pendingNewEngagementRef.current)) {
        setOpenEngagementId(pendingNewEngagementRef.current);
        // Creating one via "+" is an explicit pick, so it gets the same protection a dropdown
        // pick does. Without this the ref is consumed on THIS run and the very next run of this
        // effect — `engagements` changes again as the property refetch settles — falls through to
        // the initialEngagementId branch below and snaps the selection back to whatever was
        // pinned before. The new engagement appeared in the list but never stayed selected.
        userBackedRef.current = true;
        pendingEngagementCtx?.setPendingEngagement({ propertyId, engagementId: pendingNewEngagementRef.current });
        pendingNewEngagementRef.current = null;
        return;
      }
      return; // still waiting for re-fetch to include the new engagement — don't clear
    }
    if (initialEngagementId && !userBackedRef.current) {
      if (engagements.some(e => e.engagement_id === initialEngagementId)) {
        if (openEngagementId !== initialEngagementId) setOpenEngagementId(initialEngagementId);
        return;
      }
      if (!engagements.length) return; // this property's engagements haven't loaded yet — wait
    }
    // Clear a stale openEngagementId if the referenced engagement no longer exists in the list
    // (e.g. engagement deleted, or property switched).
    if (openEngagementId && !engagements.some(e => e.engagement_id === openEngagementId)) {
      setOpenEngagementId(null);
      return;
    }
    // Auto-select the first engagement when none is explicitly open. This keeps openEngagementId
    // as the single source of truth — menuEngagement, expandQuickQuote, expandTransitionCode etc.
    // all key off openEngagementId and would silently no-op when it is null.
    if (!openEngagementId && engagements.length >= 1 && !userBackedRef.current) {
      setOpenEngagementId(engagements[0].engagement_id);
    }
  }, [tab, engagements, openEngagementId, initialEngagementId]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {isLoading && (
        <div className="flex justify-center py-12"><Spinner /></div>
      )}
      {isError && (
        <div className="flex flex-col h-full min-h-0">
          {!hideBackButton && (
            <div className="flex-shrink-0 px-1 pt-1 pb-1.5 border-b border-ui-border">
              <button
                onClick={onBack}
                className="flex items-center justify-center h-9 w-9 rounded-control bg-ui-hover text-ui-text2 transition-colors btn-icon-hover"
                title="Back"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
            </div>
          )}
          <div className="flex flex-col items-center justify-center flex-1 gap-2 text-center px-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ui-text3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <p className="text-sm font-medium text-ui-text2">Nothing to show</p>
          </div>
        </div>
      )}
      {property && (
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Header — only property address pinned here; contact + engagement row scroll below */}
          <div className="flex-shrink-0 px-1 pt-0.5 pb-1.5 border-b border-ui-border">
            <div className="flex items-start gap-1.5">
              {!hideBackButton && (
                <button
                  onClick={onBack}
                  className="flex-shrink-0 flex items-center justify-center h-[26px] w-[26px] rounded-chip bg-ui-hover text-ui-text2 transition-colors mt-0.5 btn-icon-hover"
                  title="Back to list"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
              )}
              <div className="min-w-0 flex-1 flex flex-col gap-1">
                  <>
                    <div className="flex items-start gap-1.5">
                      <div
                        className="flex-1 min-w-0 flex items-baseline gap-1 cursor-pointer"
                        onClick={() => setAddressExpanded(v => !v)}
                        title={property.address.display_line || [property.address.street_number, property.address.street_name, property.address.street_suffix, `${property.address.city}, ${property.address.state} ${property.address.postal_code}`].filter(Boolean).join(', ')}
                      >
                        {property.property_type === 'commercial'
                          ? <svg className="flex-shrink-0 text-ui-text3 self-center" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="15" rx="1"/><path d="M16 7V5a2 2 0 0 0-4 0v2"/><line x1="12" y1="12" x2="12" y2="12.01"/></svg>
                          : <svg className="flex-shrink-0 text-ui-text3 self-center" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5 12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z"/></svg>
                        }
                        <span className={`text-base font-bold text-ui-text ${addressExpanded ? '' : 'truncate'}`}>{[property.address.street_number, property.address.pre_direction, property.address.street_name, property.address.street_suffix, property.address.post_direction].filter(Boolean).join(' ')}{property.address.unit_number ? `, ${property.address.unit_number}` : ''}</span>
                        <span className="text-xs text-ui-text3 whitespace-nowrap flex-shrink-0">{`${property.address.city}, ${property.address.state} ${property.address.postal_code}`}</span>
                      </div>
                      {engagements.length > 0 && (
                        <StartEngagementInline propertyId={propertyId} addressLabel={property.address.display_line || undefined} label="+" onCreated={(id) => { pendingNewEngagementRef.current = id; }} />
                      )}
                      {/* ⋮ property menu — Details + future actions */}
                      <div ref={propertyMenuRef} className="relative flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setPropertyMenuOpen(v => !v)}
                          className="w-[26px] h-[26px] flex items-center justify-center rounded-full text-ui-text3 hover:bg-ui-hover transition-colors btn-icon-hover"
                          title="More options"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/>
                          </svg>
                        </button>
                        {previewMediaUrl && (
                          <div
                            className="fixed inset-0 z-popover bg-black/85 flex items-center justify-center p-4"
                            onClick={() => setPreviewMediaUrl(null)}
                          >
                            <img
                              src={previewMediaUrl}
                              alt=""
                              className="max-w-full max-h-full object-contain rounded-chip shadow-pop"
                              onClick={e => e.stopPropagation()}
                            />
                            <button
                              type="button"
                              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                              onClick={() => setPreviewMediaUrl(null)}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                        )}
                        {propertyMenuOpen && (
                          <div className="absolute top-8 right-0 z-dropdown flex min-w-[150px] flex-col rounded-card border border-ui-border bg-ui-card p-1 shadow-pop">
                            <button
                              type="button"
                              onClick={() => { setPropertyMenuOpen(false); setPropertyInfoOpen(true); }}
                              className="rounded-[5px] px-2.5 py-[7px] text-left text-xs text-ui-text hover:bg-ui-hover transition-colors"
                            >
                              Property info
                            </button>
                            <button
                              type="button"
                              onClick={() => { setPropertyMenuOpen(false); setTeamModalOpen(true); }}
                              className="rounded-[5px] px-2.5 py-[7px] text-left text-xs text-ui-text hover:bg-ui-hover transition-colors"
                            >
                              Team
                            </button>
                          </div>
                        )}
                        {propertyInfoOpen && (() => {
                          const photos = media.filter(m => m.media_type === 'photo');
                          const nonPhotoMedia = media.filter(m => m.media_type !== 'photo');
                          const measurementReportFiles = measurements.filter(m => m.report_url).map(m => ({
                            id: m.measurement_id,
                            name: m.report_filename || 'Measurement Report',
                            url: m.report_url!,
                            isImage: false,
                          }));
                          const otherFiles = nonPhotoMedia.map(m => ({
                            id: m.media_id,
                            name: m.filename || m.media_type,
                            url: m.s3_url,
                            isImage: m.media_type === 'video' ? false : /\.(png|jpg|jpeg|gif|webp)$/i.test(m.filename || ''),
                          }));
                          const allFiles = [...measurementReportFiles, ...otherFiles];
                          const refMeasurement = measurements.find(m => m.is_current_reference) ?? measurements[0];
                          const validContacts = propertyContacts.filter(c => c.contact);
                          const SectionHead = ({ label, count }: { label: string; count: number }) => (
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold uppercase tracking-wider text-ui-text3">{label}</span>
                              {count > 0 && <span className="text-xs font-semibold text-accent-text bg-accent/10 rounded-full px-2 py-0.5">{count}</span>}
                            </div>
                          );
                          return (
                            <Modal
                              title="Property info"
                              onClose={() => setPropertyInfoOpen(false)}
                              maxWidthClassName="max-w-md"
                              bodyClassName="flex flex-col gap-3.5 space-y-0"
                            >
                              <span className="text-xs text-ui-text3 truncate">{property.address.display_line || [property.address.street_number, property.address.street_name, property.address.street_suffix, `${property.address.city}, ${property.address.state} ${property.address.postal_code}`].filter(Boolean).join(', ')}</span>
                              {/* Contacts */}
                              <div>
                                <SectionHead label="Contacts" count={validContacts.length} />
                                {validContacts.length === 0 ? (
                                  <p className="text-sm text-ui-text3 italic">No contacts added</p>
                                ) : (
                                  <div className="space-y-2">
                                    {validContacts.map(c => (
                                      <div key={c.property_contact_id} className="bg-ui-hover rounded-control px-3 py-2.5 flex flex-col gap-0.5">
                                        <div className="flex items-center justify-between gap-2">
                                          <span className="text-sm font-medium text-ui-text">{c.contact!.display_name}</span>
                                          {c.relationship_type && <span className="text-xs text-ui-text3 capitalize shrink-0">{c.relationship_type.replace(/_/g, ' ')}</span>}
                                        </div>
                                        {c.contact!.phones?.[0]?.value && (
                                          <a href={`tel:${c.contact!.phones[0].value}`} className="text-sm text-accent-text" onClick={e => e.stopPropagation()}>
                                            {c.contact!.phones[0].value}
                                          </a>
                                        )}
                                        {c.contact!.emails?.[0]?.value && (
                                          <a href={`mailto:${c.contact!.emails[0].value}`} className="text-xs text-ui-text3 truncate" onClick={e => e.stopPropagation()}>
                                            {c.contact!.emails[0].value}
                                          </a>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {/* Measurements */}
                              {measurements.length > 0 && refMeasurement && (
                                <div>
                                  <SectionHead label="Measurements" count={measurements.length} />
                                  <div className="grid grid-cols-3 gap-2">
                                    {refMeasurement.values.squares != null && (
                                      <div className="bg-ui-hover rounded-control px-2 py-3 text-center">
                                        <p className="text-lg font-bold text-ui-text leading-none">{refMeasurement.values.squares}</p>
                                        <p className="text-xs text-ui-text3 mt-1">squares</p>
                                      </div>
                                    )}
                                    {refMeasurement.values.total_area_sqft != null && (
                                      <div className="bg-ui-hover rounded-control px-2 py-3 text-center">
                                        <p className="text-lg font-bold text-ui-text leading-none">{Math.round(refMeasurement.values.total_area_sqft)}</p>
                                        <p className="text-xs text-ui-text3 mt-1">sq ft</p>
                                      </div>
                                    )}
                                    {refMeasurement.values.pitch && (
                                      <div className="bg-ui-hover rounded-control px-2 py-3 text-center">
                                        <p className="text-lg font-bold text-ui-text leading-none">{refMeasurement.values.pitch}</p>
                                        <p className="text-xs text-ui-text3 mt-1">pitch</p>
                                      </div>
                                    )}
                                  </div>
                                  {refMeasurement.how_obtained && (
                                    <p className="text-xs text-ui-text3 mt-2 capitalize">{refMeasurement.how_obtained.replace(/_/g, ' ')}{refMeasurement.measured_on ? ` · ${new Date(refMeasurement.measured_on).toLocaleDateString()}` : ''}</p>
                                  )}
                                </div>
                              )}
                              {/* Notes */}
                              {propNotes.length > 0 && (
                                <div>
                                  <SectionHead label="Notes" count={propNotes.length} />
                                  <div className="space-y-1.5">
                                    {propNotes.slice(0, 8).map(n => (
                                      <div key={n.note_id} className="flex items-start gap-2 bg-ui-hover rounded-chip px-3 py-2">
                                        <svg className="flex-shrink-0 mt-0.5 text-ui-text3" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                        <span className="text-sm text-ui-text leading-tight line-clamp-1">{n.title || n.note_type.replace(/_/g, ' ')}</span>
                                      </div>
                                    ))}
                                    {propNotes.length > 8 && <p className="text-xs text-ui-text3 pl-1">+{propNotes.length - 8} more</p>}
                                  </div>
                                </div>
                              )}
                              {/* Photos */}
                              {photos.length > 0 && (
                                <div>
                                  <SectionHead label="Photos" count={photos.length} />
                                  <div className="grid grid-cols-3 gap-1.5">
                                    {photos.slice(0, 9).map((p, i) => (
                                      <button
                                        key={p.media_id}
                                        type="button"
                                        className="aspect-square rounded-control overflow-hidden bg-ui-hover hover:opacity-90 transition-opacity relative"
                                        onClick={e => { e.stopPropagation(); setPreviewMediaUrl(p.s3_url); }}
                                      >
                                        <img src={p.s3_url} alt={p.caption || ''} className="w-full h-full object-cover" loading="lazy" />
                                        {i === 8 && photos.length > 9 && (
                                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                            <span className="text-sm font-semibold text-white">+{photos.length - 9}</span>
                                          </div>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {/* Files */}
                              {allFiles.length > 0 && (
                                <div>
                                  <SectionHead label="Files" count={allFiles.length} />
                                  <div className="space-y-1.5">
                                    {allFiles.map(f => (
                                      <button
                                        key={f.id}
                                        type="button"
                                        className="w-full flex items-center gap-2.5 text-left bg-ui-hover hover:bg-ui-border rounded-control px-3 py-2.5 transition-colors"
                                        onClick={e => {
                                          e.stopPropagation();
                                          if (f.isImage) { setPreviewMediaUrl(f.url); }
                                          else { window.open(f.url, '_blank', 'noopener'); }
                                        }}
                                      >
                                        <svg className="flex-shrink-0 text-ui-text3" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                        <span className="text-sm text-accent-text truncate flex-1">{f.name}</span>
                                        <svg className="flex-shrink-0 text-ui-text3" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {validContacts.length === 0 && measurements.length === 0 && propNotes.length === 0 && photos.length === 0 && allFiles.length === 0 && (
                                <p className="text-sm text-ui-text3 italic text-center py-4">No data yet</p>
                              )}
                            </Modal>
                          );
                        })()}
                        {teamModalOpen && (() => {
                          const teamMembers = menuEngagement ? [
                            { label: 'Project Manager', name: menuEngagement.project_manager_name, phone: menuEngagement.project_manager_phone },
                            { label: 'Sales Agent', name: menuEngagement.sales_agent_name, phone: menuEngagement.sales_agent_phone },
                            { label: 'Crew Lead', name: menuEngagement.assigned_user_name, phone: menuEngagement.assigned_user_phone },
                          ].filter(m => m.name) : [];
                          const initials = (name: string) => name.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();
                          return (
                            <Modal
                              title="Team"
                              onClose={() => setTeamModalOpen(false)}
                              maxWidthClassName="max-w-md"
                            >
                              <p className="text-xs text-ui-text3 truncate">
                                {[property.address.display_line || [property.address.street_number, property.address.street_name].filter(Boolean).join(' '), menuEngagement?.title].filter(Boolean).join(' · ')}
                              </p>
                              {!openEngagementId ? (
                                <p className="text-sm text-ui-text3 italic text-center py-2">No active engagement selected</p>
                              ) : !menuEngagement ? (
                                <p className="text-sm text-ui-text3 text-center py-2">Loading…</p>
                              ) : teamMembers.length === 0 ? (
                                <p className="text-sm text-ui-text3 italic text-center py-2">No team members assigned yet</p>
                              ) : (
                                <div className="space-y-2">
                                  {teamMembers.map(({ label, name, phone }) => (
                                    <div key={label} className="flex items-center gap-3 bg-ui-hover rounded-control px-3 py-3">
                                      <div className="w-9 h-9 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0">
                                        <span className="text-sm font-bold text-accent-text">{initials(name!)}</span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-ui-text truncate">{name}</p>
                                        <p className="text-xs text-ui-text3">{label}</p>
                                        {phone && (
                                          <a href={`tel:${phone}`} className="text-xs text-accent-text" onClick={e => e.stopPropagation()}>{phone}</a>
                                        )}
                                      </div>
                                      {phone && (
                                        <a
                                          href={`tel:${phone}`}
                                          className="flex-shrink-0 w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent-text hover:bg-accent/20 transition-colors"
                                          onClick={e => e.stopPropagation()}
                                        >
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.38 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </Modal>
                          );
                        })()}
                      </div>
                    </div>
                    {engagements.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-ui-text3">Work type</span>
                        <div className="relative min-w-40 self-start" ref={engagementDropdownRef}>
                        <button
                          type="button"
                          onClick={() => {
                            if (tab !== 'engagement') {
                              setTab('engagement');
                            } else if (engagements.length > 1) {
                              setEngagementDropdownOpen(v => !v);
                            }
                          }}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-chip border text-xs font-semibold transition-colors max-w-full ${
                            tab === 'engagement'
                              ? 'bg-accent/10 text-accent-text border-accent/40'
                              : 'bg-ui-subtle text-ui-text2 border-ui-border hover:bg-ui-hover'
                          }`}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                          <span className="min-w-0 truncate">
                            {openEngagementId
                              ? (engagements.find(e => e.engagement_id === openEngagementId)?.title ?? 'Engagement')
                              : `Engagement (${engagements.length})`}
                          </span>
                          {engagements.length > 1 && (
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`flex-shrink-0 transition-transform ${engagementDropdownOpen ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"/></svg>
                          )}
                        </button>
                        {engagementDropdownOpen && (
                          <div className="absolute top-full left-0 mt-1 z-dropdown bg-ui-card rounded-control border border-ui-border shadow-pop overflow-y-auto max-h-64 min-w-[180px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                            {engagements.map(e => (
                              <button
                                key={e.engagement_id}
                                type="button"
                                onClick={() => {
                                  setOpenEngagementId(e.engagement_id);
                                  setTab('engagement');
                                  setEngagementDropdownOpen(false);
                                  userBackedRef.current = true;
                                  pendingEngagementCtx?.setPendingEngagement({ propertyId, engagementId: e.engagement_id });
                                  onUserView?.({
                                    module: 'properties',
                                    params: { initial_engagement_id: e.engagement_id },
                                    recordId: propertyId,
                                    title: property?.address?.display_line ? `${e.title} · ${property.address.display_line}` : e.title,
                                    subtitle: e.current_status,
                                  });
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs text-left transition-colors hover:bg-ui-hover ${
                                  e.engagement_id === openEngagementId ? 'text-accent-text font-semibold bg-accent/5' : 'text-ui-text'
                                }`}
                              >
                                <span className="flex-1 min-w-0 truncate">{e.title}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                        {!stageRailVisible && tab === 'engagement' && openEngagementId && openWorkflowState && (
                          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/10 text-accent-text text-xs font-semibold whitespace-nowrap">
                            <span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />
                            {openWorkflowState.display_labels.stage}
                          </span>
                        )}
                      </div>
                    )}
                  </>

              </div>
            </div>
          </div>


          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto no-scrollbar">
            <div className="flex flex-col gap-3 pt-2 pb-3">
              {displayPoc && (
                <ContactRow
                  contact={displayPoc}
                  compact
                  extraPhoneCount={extraContactCount}
                  onExtraClick={extraContactCount > 0 ? () => setTab('media') : undefined}
                />
              )}
              {/* Roof Estimate — satellite view + AI sqft for this property's own address, always
                  visible regardless of the active tab (same reasoning as the contact row above:
                  it's about the property itself, not the engagement/media view). */}
              {tab === 'media' ? (
                <PropertyMediaTab
                  property={property}
                  propertyId={propertyId}
                  media={media}
                  measurements={measurements}
                  propNotes={propNotes}
                  contacts={propertyContacts}
                  engagementHolder={engagements.find(e => e.engagement_id === openEngagementId)?.holder_name ?? null}
                />
              ) : (
                <PropertyEngagementTab
                  propertyId={propertyId}
                  engagements={engagements}
                  openEngagementId={openEngagementId}
                  onOpenEngagement={(id) => {
                    // Any explicit pick here — "Back to engagements" (null) or a specific card
                    // from the picker list — is a deliberate user override, same as the header
                    // dropdown above; see the fix note there for what goes wrong without this.
                    userBackedRef.current = true;
                    setOpenEngagementId(id);
                    if (id !== null) {
                      const picked = engagements.find(e => e.engagement_id === id);
                      pendingEngagementCtx?.setPendingEngagement({ propertyId, engagementId: id });
                      onUserView?.({
                        module: 'properties',
                        params: { initial_engagement_id: id },
                        recordId: propertyId,
                        title: property?.address?.display_line && picked ? `${picked.title} · ${property.address.display_line}` : picked?.title,
                        subtitle: picked?.current_status,
                      });
                    }
                  }}
                  onSelectTask={onSelectTask}
                  engagementRev={engagementRev}
                  expandQuickQuote={
                    !!expandQuickQuoteEngagementId && expandQuickQuoteEngagementId === openEngagementId
                  }
                  openQuickQuoteMapEngagementId={openQuickQuoteMapEngagementId}
                  quickQuoteScrollTick={
                    expandQuickQuoteEngagementId === openEngagementId ? quickQuoteScrollTick : undefined
                  }
                  expandTransitionCode={
                    expandTransitionEngagementId === openEngagementId ? expandTransitionCode : undefined
                  }
                  expandAssessmentCaptureTick={
                    expandAssessmentCaptureEngagementId === openEngagementId ? expandAssessmentCaptureTick : undefined
                  }
                  openProposalPreparedView={
                    proposalPreparedViewRequest
                      ? { view: proposalPreparedViewRequest.view, tick: proposalPreparedViewRequest.tick }
                      : null
                  }
                  proposalSurfaceInteract={proposalSurfaceInteractRequest ?? null}
                  onStageRailVisibilityChange={setStageRailVisible}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Bridges the Engagements list ("open the first one") into WorkspacePropertyDetail — an
 * Engagement is always viewed through its property (see root CLAUDE.md's Property/Transaction
 * model), so opening one from a flat engagements list should never show a raw field dump; it
 * should land on the same property card + Engagement tab as opening from the Properties list. */
function EngagementAsPropertyDetail({ engagementId, records, onBack, onSelectTask, expandQuickQuoteEngagementId, openQuickQuoteMapEngagementId, quickQuoteScrollTick, expandTransitionEngagementId, expandTransitionCode, expandAssessmentCaptureEngagementId, expandAssessmentCaptureTick }: {
  engagementId: string;
  records: Rec[];
  onBack: () => void;
  onSelectTask?: (id: string) => void;
  expandQuickQuoteEngagementId?: string | null;
  openQuickQuoteMapEngagementId?: string | null;
  quickQuoteScrollTick?: number;
  expandTransitionEngagementId?: string | null;
  expandTransitionCode?: string | null;
  expandAssessmentCaptureEngagementId?: string | null;
  expandAssessmentCaptureTick?: number;
}) {
  const record = records.find(r => sv(r.engagement_id) === engagementId);
  const propertyId = record ? sv(record.property_id) : '';
  if (!propertyId) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <button onClick={onBack} className="flex-shrink-0 flex items-center gap-1.5 px-1 py-2 text-xs text-accent-text hover:opacity-80 transition-opacity">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to list
        </button>
        <p className="text-center text-danger-text py-8 text-sm">Could not find this engagement's property.</p>
      </div>
    );
  }
  return (
    <WorkspacePropertyDetail
      propertyId={propertyId}
      initialEngagementId={engagementId}
      onBack={onBack}
      onSelectTask={onSelectTask}
      expandQuickQuoteEngagementId={expandQuickQuoteEngagementId}
      openQuickQuoteMapEngagementId={openQuickQuoteMapEngagementId}
      quickQuoteScrollTick={quickQuoteScrollTick}
      expandTransitionEngagementId={expandTransitionEngagementId}
      expandTransitionCode={expandTransitionCode}
      expandAssessmentCaptureEngagementId={expandAssessmentCaptureEngagementId}
      expandAssessmentCaptureTick={expandAssessmentCaptureTick}
    />
  );
}


/** Media tab body — Photos, Measurements, Notes, minimal timeline (property created +
 *  engagement milestones only). All uploads attach directly to the property; no engagement
 *  gating (backend now accepts engagement_id=null on media/measurements). */
function PropertyMediaTab({ property, propertyId, media, measurements, propNotes, contacts, engagementHolder }: {
  property: NonNullable<ReturnType<typeof useGetPropertyDetailQuery>['data']>;
  propertyId: string;
  media: Array<{ media_id: string; media_type: string; s3_url: string; filename?: string | null; classifications: string[]; caption?: string | null }>;
  measurements: Array<{
    measurement_id: string; how_obtained: string; provider?: string | null; measured_on?: string | null;
    is_current_reference: boolean;
    values: { squares?: number | null; pitch?: string | null; ridge_length_ft?: number | null; total_area_sqft?: number | null };
    report_url?: string | null; report_filename?: string | null; notes?: string | null;
  }>;
  propNotes: Array<{ note_id: string; title?: string | null; note_type: string; source?: string | null; audio_url?: string | null; final_transcript?: string | null; clean_transcript?: string | null; review_summary?: string | null; created_on?: string | null }>;
  contacts: import('@/redux/internal-services/workspaceApiExtensions').PropertyContactLink[];
  engagementHolder?: string | null;
}) {
  const activeContacts = contacts.filter(c => c.is_current && c.contact);
  return (
    <>
      {engagementHolder && (
        <div className="flex items-center justify-between rounded-control border border-ui-border bg-ui-subtle px-3 py-2">
          <span className="text-xs text-ui-text3">Engagement Holder</span>
          <span className="text-xs font-medium text-ui-text">{engagementHolder}</span>
        </div>
      )}
      {property.special_instructions && (
        <div className="rounded-chip border border-accent/40 bg-accent/5 px-3 py-2 text-xs text-ui-text2 flex items-start gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          {property.special_instructions}
        </div>
      )}

      {/* Contacts section — full list of all linked contacts for this property. */}
      {activeContacts.length > 0 && (
        <CardSection title="Contacts" count={activeContacts.length}>
          <div className="flex flex-col gap-2">
            {activeContacts.map(c => (
              <ContactRow
                key={c.contact_id}
                contact={{
                  name: c.contact!.display_name,
                  role: c.relationship_type || '',
                  is_primary: false,
                  phone: c.contact!.phones?.[0]?.value ?? null,
                  email: c.contact!.emails?.[0]?.value ?? null,
                }}
                extraPhoneCount={Math.max(0, (c.contact!.phones?.length ?? 1) - 1)}
              />
            ))}
          </div>
        </CardSection>
      )}

      <PropertyPhotosSection propertyId={propertyId} media={media} readOnly />
      <PropertyMeasurementsSection
        propertyId={propertyId}
        currentMeasurement={property.current_measurement}
        measurements={measurements}
        readOnly
      />
      <PropertyNotesSection propertyId={propertyId} notes={propNotes} readOnly />
      <PropertyFilesSection propertyId={propertyId} media={media} readOnly />

      {/* Minimal timeline — the property's created date + engagement milestones only. */}
      <div className="flex flex-col gap-2 px-1 pt-1">
        <h3 className="text-sm font-semibold text-ui-text">Timeline</h3>
        <TimelineList
          entries={buildMediaTabTimeline(property)}
        />
      </div>
    </>
  );
}

/** Rendered inside the Engagement tab when a specific engagement is opened. Wraps the
 *  common WorkflowCard with engagement-scoped upload surfaces (Photos, Files, Measurements)
 *  — these only render while that engagement is open, and everything uploaded from here
 *  carries the engagement_id. */
function EngagementDetailPanel({ propertyId, engagementId, onSelectTask, rev, expandQuickQuote, openQuickQuoteMapEngagementId, quickQuoteScrollTick, expandTransitionCode, expandAssessmentCaptureTick, openProposalPreparedView, proposalSurfaceInteract, onStageRailVisibilityChange }: {
  propertyId: string;
  engagementId: string;
  onSelectTask?: (id: string) => void;
  rev?: number;
  expandQuickQuote?: boolean;
  openQuickQuoteMapEngagementId?: string | null;
  quickQuoteScrollTick?: number;
  /** transition_code whose business_capture form should auto-open on this engagement's
   *  WorkflowCard (already gated to this engagement_id by the caller). */
  expandTransitionCode?: string | null;
  /** already gated to this engagement_id by the caller (see expandAssessmentCaptureEngagementId
   *  comparison at WorkspacePropertyDetail). */
  expandAssessmentCaptureTick?: number;
  openProposalPreparedView?: { view: 'preview_proposal' | 'view_estimate' | 'open_assessment'; tick: number } | null;
  proposalSurfaceInteract?: ProposalSurfaceInteractPayload & { tick: number } | null;
  onStageRailVisibilityChange?: (visible: boolean) => void;
}) {
  const { data: property } = useGetPropertyDetailQuery(propertyId);
  const { data: allMedia = [] } = useGetPropertyMediaQuery(propertyId);
  const { data: measurements = [] } = useGetPropertyMeasurementsQuery(propertyId);
  // Note: engagement-scoped notes are now fetched lazily inside WorkflowCard's Notes modal.
  const { data: workflowState } = useGetEngagementWorkflowStateQuery(engagementId, { skip: !engagementId });
  // A closed engagement is greyed out as a whole (rail, status row, card, sections) - QA #16.
  const { data: engagementDoc } = useGetEngagementByIdQuery(engagementId, { skip: !engagementId });
  const closedLook = closedCardClass(!!engagementDoc?.closed_early);
  // Workflow transitions on this engagement create context_type='engagement' tasks — fetch
  // them here so anything the workflow spawned surfaces on the engagement it belongs to.
  // current_workflow_status_code scopes the checklist to what's still relevant: a still-OPEN
  // task always shows regardless of which status created it (some gates check a task by source
  // tag several transitions later — see TaskDAO.list), only a COMPLETED/CANCELLED task tied to
  // an already-passed status gets hidden, so the checklist stops re-showing done history once
  // the engagement moves on without risking hiding something a later gate still needs.
  const { data: taskList } = useGetTasksListQuery({
    context_type: 'engagement', context_entity_id: engagementId,
    current_workflow_status_code: workflowState?.current_status_code, limit: 50,
    mine: true, my_queues: true,
  });
  const engTasks = taskList?.data ?? [];
  // Filter to just this engagement's artifacts — the same property-scoped lists are shared
  // across the property, so scope by engagement_id before handing to the sections.
  const engMedia = allMedia.filter(m => m.engagement_id === engagementId);
  const engMeasurements = measurements.filter(m => m.source_engagement_id === engagementId);
  const currentMeas = property?.current_measurement && engMeasurements.some(m => m.measurement_id === property.current_measurement?.measurement_id)
    ? property.current_measurement
    : undefined;

  // Which sections are actually required at this point in the workflow — driven by governance
  // gates on the engagement's currently-available transitions (RTK Query dedupes against
  // WorkflowCard's identical calls, no extra network round-trip). No gate for a section on any
  // available transition = that section isn't relevant right now, so it's hidden rather than
  // shown empty. Notes has no corresponding gate concept yet, so it always shows.
  const { data: availableTransitions = [] } = useListAvailableTransitionsQuery(engagementId, { skip: !engagementId });
  const { data: customization } = useGetWorkflowCustomizationQuery(undefined, { skip: !engagementId });
  const orderedStages = [...(customization?.stages ?? [])].sort((a, b) => a.display_order - b.display_order);
  const availableTransitionCodes = new Set(availableTransitions.map(t => t.transition_code));
  const activeGateTypes = new Set(
    (customization?.governance_gates ?? [])
      .filter(g => availableTransitionCodes.has(g.target_ref))
      .map(g => g.condition_type),
  );
  const showPhotos = activeGateTypes.has('required_photos');
  const showMeasurements = activeGateTypes.has('required_measurement');
  const showFiles = activeGateTypes.has('required_document');
  // While assessment_scheduled, AssessmentCaptureCard (inside WorkflowCard) is the one place to
  // capture measurements/notes for this engagement — its own "Add Measurement"/"Voice note"
  // actions. Showing these two standalone sections as well here is the exact duplication the user
  // flagged repeatedly; hide them for this one status rather than relocating/duplicating them.
  const isAssessmentScheduled = workflowState?.current_status_code === 'assessment_scheduled';

  const stageRailRef = useRef<HTMLDivElement>(null);
  // workflowState (and so orderedStages) arrives async, and the ref'd div below only mounts once
  // both are ready — depending on onStageRailVisibilityChange alone (a stable setState reference)
  // meant this effect ran exactly once, right after mount, while stageRailRef.current was still
  // null and bailed out for good. Re-running once the rail's own render condition flips to true
  // (or the engagement switch remounts it) is what actually attaches the observer.
  const stageRailMounted = !!workflowState && orderedStages.length > 0;
  useEffect(() => {
    const el = stageRailRef.current;
    if (!el || !onStageRailVisibilityChange) return;
    const observer = new IntersectionObserver(
      ([entry]) => { onStageRailVisibilityChange(entry.isIntersecting); },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onStageRailVisibilityChange, stageRailMounted]);

  return (
    <>
      {/* Stage rail — scrolls with content */}
      {workflowState && orderedStages.length > 0 && (
        <div ref={stageRailRef} className={`bg-ui-card px-3 pt-2.5 pb-1 ${closedLook}`}>
          <StageRail stages={orderedStages} currentStageCode={workflowState.current_stage_code} />
        </div>
      )}
      {/* Sticky status row only */}
      {workflowState && (
        <div className="sticky top-0 z-20 bg-ui-card px-3 py-2">
          {/* Greyed inside the band, not on it: opacity on the sticky band itself makes its
              background see-through, so the content scrolling underneath bled through it. */}
          <div className={`flex items-center gap-2 ${closedLook}`}>
            {/* One line at any width: the label truncates instead of wrapping the pill to two rows, and
                the "Last updated" prefix drops on phones so the two never fight for the row. */}
            <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-accent-fill text-on-accent px-3 py-1 text-xs font-semibold tracking-wide shadow-glow-accent">
              <span className="w-1.5 h-1.5 rounded-full bg-white flex-shrink-0 ring-[3px] ring-white/30" />
              <span className="truncate">{workflowState.display_labels.status}</span>
            </span>
            {workflowState.updated_on && (
              <span className="ml-auto flex-shrink-0 text-xs text-ui-text3 whitespace-nowrap">
                <span className="hidden sm:inline">Last updated </span>{relativeTime(workflowState.updated_on)}
              </span>
            )}
          </div>
        </div>
      )}
    <div className={`flex flex-col gap-3 ${closedLook}`}>
        <WorkflowCard
          engagementId={engagementId}
          rev={rev}
          tasks={engTasks}
          onSelectTask={onSelectTask}
          expandQuickQuote={expandQuickQuote}
          openQuickQuoteMapEngagementId={openQuickQuoteMapEngagementId}
          quickQuoteScrollTick={quickQuoteScrollTick}
          expandTransitionCode={expandTransitionCode}
          expandAssessmentCaptureTick={expandAssessmentCaptureTick}
          openProposalPreparedView={openProposalPreparedView}
          proposalSurfaceInteract={proposalSurfaceInteract}
          hideStageBanner={!!workflowState}
          greyedByParent
        />
      {showPhotos && <PropertyPhotosSection propertyId={propertyId} engagementId={engagementId} media={engMedia} />}
      {!isAssessmentScheduled && showMeasurements && (
        <PropertyMeasurementsSection
          propertyId={propertyId}
          engagementId={engagementId}
          currentMeasurement={currentMeas}
          measurements={engMeasurements}
        />
      )}
      {showFiles && <PropertyFilesSection propertyId={propertyId} engagementId={engagementId} media={engMedia} />}
      <EngagementMeetingsSection engagementId={engagementId} />
      <EngagementActivitySection engagementId={engagementId} />
    </div>
    </>
  );
}


interface MinimalTimelineEntry { date: string; label: string; description?: string | null; latest?: boolean }

function buildMediaTabTimeline(property: NonNullable<ReturnType<typeof useGetPropertyDetailQuery>['data']>): MinimalTimelineEntry[] {
  // Only the property creation + one entry per engagement creation — no other timeline events.
  // The PropertyDetail payload doesn't carry the property's own created_on, so the earliest
  // engagement's created_on doubles as the "Property created" marker.
  const sorted = [...property.engagements]
    .filter(e => !!e.created_on)
    .sort((a, b) => (a.created_on || '').localeCompare(b.created_on || ''));
  if (sorted.length === 0) return [];

  const entries: MinimalTimelineEntry[] = [
    { date: sorted[0].created_on as string, label: 'Property created' },
    ...sorted.map(e => ({
      date: e.created_on as string,
      label: `Engagement created: ${e.title}`,
      description: humanizeLabel(e.engagement_type),
    })),
  ];
  entries[entries.length - 1].latest = true;
  return entries;
}

function TimelineList({ entries }: { entries: MinimalTimelineEntry[] }) {
  if (entries.length === 0) return <p className="text-xs text-ui-text3 py-2">No history yet.</p>;
  return (
    <div className="flex flex-col">
      {entries.map((entry, i) => (
        <div key={i} className="flex gap-3">
          <div className="flex flex-col items-center">
            <span className="text-xs text-ui-text3 whitespace-nowrap w-16 text-right pt-0.5">{fmtDate(entry.date)}</span>
          </div>
          <div className="flex flex-col items-center">
            <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${entry.latest ? 'bg-accent' : 'bg-ui-border'}`} />
            {i < entries.length - 1 && <span className="w-px flex-1 bg-ui-border" />}
          </div>
          <div className="pb-4 min-w-0">
            <p className="text-sm font-medium text-ui-text">{entry.label}</p>
            {entry.description && <p className="text-xs text-ui-text2 mt-0.5">{entry.description}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Engagement tab body — 0 engagements = "Start Engagement" empty state; 1+ auto-opens into
 *  its WorkflowCard (first engagement when none is explicitly selected). The dropdown in the
 *  pinned header is the mechanism for switching between engagements — no picker list needed. */
function PropertyEngagementTab({ propertyId, engagements, openEngagementId, onSelectTask, engagementRev, expandQuickQuote, openQuickQuoteMapEngagementId, quickQuoteScrollTick, expandTransitionCode, expandAssessmentCaptureTick, openProposalPreparedView, proposalSurfaceInteract, onStageRailVisibilityChange }: {
  propertyId: string;
  engagements: Array<{ engagement_id: string; title: string; engagement_type: string; stage: string; current_status: string; holder_id?: string | null; holder_name?: string | null }>;
  openEngagementId: string | null;
  onOpenEngagement?: (id: string | null) => void;
  onSelectTask?: (id: string) => void;
  engagementRev?: number;
  expandQuickQuote?: boolean;
  openQuickQuoteMapEngagementId?: string | null;
  quickQuoteScrollTick?: number;
  expandTransitionCode?: string | null;
  expandAssessmentCaptureTick?: number;
  openProposalPreparedView?: { view: 'preview_proposal' | 'view_estimate' | 'open_assessment'; tick: number } | null;
  proposalSurfaceInteract?: ProposalSurfaceInteractPayload & { tick: number } | null;
  onStageRailVisibilityChange?: (visible: boolean) => void;
}) {
  if (engagements.length === 0) {
    return <StartEngagementEmpty propertyId={propertyId} />;
  }
  if (!openEngagementId) {
    // Auto-select effect in WorkspacePropertyDetail hasn't fired yet — nothing to render.
    return null;
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <EngagementDetailPanel
        propertyId={propertyId}
        engagementId={openEngagementId}
        onSelectTask={onSelectTask}
        rev={engagementRev}
        expandQuickQuote={expandQuickQuote}
        openQuickQuoteMapEngagementId={openQuickQuoteMapEngagementId}
        quickQuoteScrollTick={quickQuoteScrollTick}
        expandTransitionCode={expandTransitionCode}
        expandAssessmentCaptureTick={expandAssessmentCaptureTick}
        openProposalPreparedView={openProposalPreparedView}
        proposalSurfaceInteract={proposalSurfaceInteract}
        onStageRailVisibilityChange={onStageRailVisibilityChange}
      />
    </div>
  );
}

function StartEngagementEmpty({ propertyId, addressLabel }: { propertyId: string; addressLabel?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-3 text-center px-6">
      <div className="h-12 w-12 rounded-card bg-accent/10 text-accent-text flex items-center justify-center">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      </div>
      <p className="text-sm font-medium text-ui-text">No engagements yet</p>
      <p className="text-xs text-ui-text3">Start one to track sales, jobs, warranty events for this property.</p>
      <StartEngagementInline propertyId={propertyId} addressLabel={addressLabel} label="Start Engagement" prominent />
    </div>
  );
}

function StartEngagementInline({ propertyId, addressLabel, label = '+ Start another engagement', prominent = false, onCreated }: {
  propertyId: string;
  /** The property's one-line address. Appended to the "⋯" entry's title so it matches the label
   *  the backend builds for the same view (engagement titles are serials — "Replacement 2" alone
   *  doesn't say which property). */
  addressLabel?: string;
  label?: string;
  prominent?: boolean;
  onCreated?: (engagementId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [engagementType, setEngagementType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createEngagement, { isLoading }] = useCreateEngagementMutation();
  const dispatch = useDispatch();
  const onUserView = useContext(UserViewContext);

  const { data: catalogueData } = useListCatalogueEntriesQuery({ prefix: 'workflow_engine' });
  const engagementTypeOptions = useMemo<Array<{ id: string; label: string }>>(() => {
    const entry = catalogueData?.data.find((c) => c.key === 'workflow_engine');
    return (entry?.effective_value?.engagement_types as Array<{ id: string; label: string }> | undefined) ?? [];
  }, [catalogueData]);

  useEffect(() => {
    if (!engagementType && engagementTypeOptions.length) setEngagementType(engagementTypeOptions[0].id);
  }, [engagementTypeOptions, engagementType]);

  async function submit() {
    if (!engagementType) { setError('Choose an engagement type.'); return; }
    setError(null);
    try {
      const result = await createEngagement({ propertyId, engagementType }).unwrap();
      dispatch(workspaceApi.util.invalidateTags([{ type: 'Property', id: propertyId }]));
      setOpen(false);
      if (result?.engagement_id) onCreated?.(result.engagement_id);
      // Add the just-opened engagement to the "⋯" menu. Deliberately the SAME shape SAM's
      // create_engagement / get_engagement publish (module 'properties', record_id = property,
      // filter.initial_engagement_id) so creating one here and asking SAM for it collapse to a
      // single entry instead of two near-identical rows.
      if (result?.engagement_id) {
        onUserView?.({
          module: 'properties',
          params: { initial_engagement_id: result.engagement_id },
          recordId: propertyId,
          title: addressLabel ? `${result.title} · ${addressLabel}` : result.title,
          subtitle: result.display_labels?.status,
        });
      }
    } catch (e: unknown) {
      const msg = (e as { data?: { message?: string } })?.data?.message || 'Could not start engagement.';
      setError(msg);
    }
  }

  const form = (
    <div className="rounded-control border border-ui-border bg-ui-card p-3 flex flex-col gap-2 shadow-pop min-w-[220px]">
      <label className="flex flex-col gap-1">
        <span className="text-micro text-ui-text3 uppercase tracking-wide font-semibold">Type</span>
        <select
          value={engagementType}
          onChange={e => setEngagementType(e.target.value)}
          disabled={!engagementTypeOptions.length}
          autoFocus
          className="rounded-chip border border-ui-border bg-ui-subtle px-3 py-2 text-sm focus:outline-none focus:border-ui-ring disabled:opacity-60"
        >
          {engagementTypeOptions.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </label>
      {error && <p className="text-xs text-danger-text">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => { setOpen(false); setError(null); }} className="text-xs px-3 py-1 rounded-full text-ui-text2 hover:bg-ui-hover">Cancel</button>
        <button type="button" disabled={!engagementType || isLoading} onClick={submit} className="text-xs font-medium px-3 py-1 rounded-full btn-sheen text-on-primary disabled:opacity-40">
          {isLoading ? 'Starting…' : 'Start engagement'}
        </button>
      </div>
    </div>
  );

  if (prominent) {
    return open ? form : (
      <button type="button" onClick={() => setOpen(true)} className="text-sm font-semibold px-4 py-2 rounded-full btn-sheen text-on-primary">
        {label}
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="text-xs font-medium px-3 py-1.5 rounded-full bg-accent/10 text-accent-text hover:bg-accent/15"
      >
        {label}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-dropdown">
          {form}
        </div>
      )}
    </div>
  );
}

// ─── WorkspaceNotificationsPanel ─────────────────────────────────────────────
// Compact notification list for the workspace sidebar.
// Approvals are handled inline — clicking a pending-approval notification opens
// ApprovalDetailPanel here rather than navigating to a standalone page.

function WorkspaceNotificationsPanel({
  externalApprovalId,
  onExternalApprovalClose,
  onApprovalSelected,
  onEntityLink,
  onRowsChange,
}: {
  /** Approval to open directly (e.g. from /?approval=<id> URL param routed through ChatPage). */
  externalApprovalId?: string | null;
  onExternalApprovalClose?: () => void;
  /** Emits context when the user opens an approval detail so the agent knows which approval is on screen. */
  onApprovalSelected?: (approvalId: string | null) => void;
  /** Called when the user clicks an entity link on a notification (e.g. linked property / task). */
  onEntityLink?: (module: string, entityId: string, entityType?: string) => void;
  /** The rows now listed (tab filter + loaded pages) — so SAM's card_context matches the screen. */
  onRowsChange?: (filter: Record<string, unknown>, rows: Record<string, unknown>[]) => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'all' | 'unread' | 'action' | 'archived'>('all');
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(externalApprovalId ?? null);
  const NOTIFICATIONS_PAGE_SIZE = 20;
  const [skip, setSkip] = useState(0);

  // Reset pagination whenever the active tab changes
  useEffect(() => {
    setSkip(0);
  }, [tab]);

  // Sync when the external approval changes (e.g. new ?approval= param while panel is already open)
  useEffect(() => {
    if (externalApprovalId) setSelectedApprovalId(externalApprovalId);
  }, [externalApprovalId]);

  const { data: approvalDetail, isLoading: approvalLoading } = useGetApprovalByIdQuery(
    selectedApprovalId!,
    { skip: !selectedApprovalId },
  );

  // An approval notification should land the user on the engagement workspace directly —
  // no intermediate approval-detail screen. Redirect as soon as the approval resolves.
  useEffect(() => {
    if (approvalDetail?.engagement_id && onEntityLink) {
      onEntityLink('WFL', approvalDetail.engagement_id, 'engagement');
      setSelectedApprovalId(null);
      onExternalApprovalClose?.();
      onApprovalSelected?.(null);
    }
  }, [approvalDetail, onEntityLink, onExternalApprovalClose, onApprovalSelected]);

  const queryParams = {
    ...(tab === 'unread' ? { read: false } : {}),
    ...(tab === 'action' ? { action_required: true } : {}),
    archived: tab === 'archived',
    limit: NOTIFICATIONS_PAGE_SIZE,
    skip,
  };
  const { data, isFetching } = useGetInboxQuery(queryParams);
  const { data: counts } = useGetInboxCountsQuery();

  // Accumulate pages locally since RTK Query treats each skip value as a separate cache entry.
  const [accumulatedItems, setAccumulatedItems] = useState<InAppNotificationOut[]>([]);
  useEffect(() => {
    if (!data) return;
    setAccumulatedItems((prev) => (skip === 0 ? data.items : [...prev, ...data.items]));
  }, [data, skip]);

  const items = accumulatedItems;
  useEffect(() => {
    if (!data) return;
    onRowsChange?.({ tab }, accumulatedItems.map(n => ({ ...n, notification_id: n.id })));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- report on loaded rows only
  }, [accumulatedItems]);
  const total = data?.total ?? 0;
  const hasMore = items.length < total;

  const tabs: Array<{ id: 'all' | 'unread' | 'action' | 'archived'; label: string }> = [
    { id: 'all', label: t('notifications.filterAll') },
    { id: 'unread', label: counts?.unread ? `${t('notifications.filterUnread')} (${counts.unread})` : t('notifications.filterUnread') },
    { id: 'action', label: counts?.action_required ? `${t('notifications.filterActionRequired')} (${counts.action_required})` : t('notifications.filterActionRequired') },
    { id: 'archived', label: t('notifications.filterArchived') },
  ];

  // ── Inline approval detail view ───────────────────────────────────────────
  if (selectedApprovalId) {
    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => { setSelectedApprovalId(null); onExternalApprovalClose?.(); onApprovalSelected?.(null); }}
          className="flex items-center gap-1.5 text-xs font-medium text-accent-text hover:underline self-start"
        >
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to notifications
        </button>
        {approvalLoading ? (
          <div className="flex justify-center py-8"><Spinner size="sm" /></div>
        ) : approvalDetail ? (
          <ApprovalDetailPanel
            approval={approvalDetail}
            onOpenEngagement={onEntityLink ? (id) => onEntityLink('WFL', id, 'engagement') : undefined}
          />
        ) : (
          <p className="text-sm text-ui-text2 text-center py-8">Approval not found or no longer available.</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-2.5 py-1 rounded-chip text-xs font-medium transition-colors ${
              tab === t.id ? 'bg-accent-fill text-on-accent' : 'text-ui-text2 hover:bg-ui-hover'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {isFetching && items.length === 0 ? (
        <div className="flex justify-center py-8"><Spinner size="sm" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center gap-1">
          <p className="text-sm font-medium text-ui-text2">
            {tab === 'archived' ? t('notifications.noArchivedNotifications') : t('notifications.noUnread')}
          </p>
          <p className="text-xs text-ui-text3">
            {tab === 'archived' ? t('notifications.archivedEmpty') : t('notifications.allCaughtUp')}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-ui-border rounded-control border border-ui-border bg-ui-card overflow-hidden">
          {items.map((n) => {
            const approvalMatch = n.action_route?.match(/^\/workflow\/approvals\/(.+)$/);
            const approvalId = approvalMatch ? approvalMatch[1] : null;
            const hasPendingApproval = n.is_action_required && n.action_status === 'Pending' && !!approvalId;
            const hasEntityLink = !!onEntityLink && !!n.source_entity_id && !!n.source_module && !hasPendingApproval;
            // Some notifications are ABOUT an entity that has no workspace card of its own — an
            // assessment, for instance, is read inside its engagement's workflow card. Those carry
            // the real destination in action_route while source_entity stays the thing the
            // notification is genuinely about, which is what makes it useful provenance. Honour
            // the route when it names an engagement, so the click lands on that engagement rather
            // than falling through to a card lookup for an id no card knows.
            const routeEngagementId = !hasPendingApproval
              ? n.action_route?.match(/^\/chat\?engagement=([^&]+)$/)?.[1]
              : undefined;
            const entityAction = routeEngagementId && onEntityLink
              ? () => onEntityLink('WFL', routeEngagementId, 'engagement')
              : hasEntityLink
                ? () => onEntityLink!(n.source_module!, n.source_entity_id!, n.source_entity_type ?? undefined)
                : undefined;
            return (
              <div key={n.id}>
                <NotificationItem
                  notification={n}
                  compact
                  disableNavigation
                  onAction={hasPendingApproval ? () => { setSelectedApprovalId(approvalId!); onApprovalSelected?.(approvalId!); } : entityAction}
                />
              </div>
            );
          })}
        </div>
      )}
      {items.length > 0 && hasMore && (
        <button
          type="button"
          onClick={() => setSkip((prev) => prev + NOTIFICATIONS_PAGE_SIZE)}
          disabled={isFetching}
          className="self-center px-3 py-1.5 rounded-chip text-xs font-medium text-accent-text hover:bg-ui-hover disabled:opacity-50"
        >
          {isFetching ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

// ─── UXView ───────────────────────────────────────────────────────────────────

export const UXView = ({
  cards, messageStrip, onSave, onFacet, openCards, setOpenCards, openCardSources, onExpandStrip,
  isWaiting,
  history = [], onReplay, historyHasMore, onHistoryLoadMore, onActiveMeeting: _onActiveMeeting, onActiveTask: _onActiveTask, onNoteSynced, onStartNoteTaker,
  onNotesListChanged, onMyTodosChanged, openApprovalId, onCloseApproval, onUserOpenCard, onApprovalSelected,
  openNotificationsPanel, onCloseNotificationsPanel, emptyNote, onOpenActionView, onUserView, onUserList,
  directEngagementId: directEngagementIdProp = null, onDirectEngagementChange,
  pendingEngagement: pendingEngagementProp = null, onPendingEngagementChange,
  expandQuickQuoteEngagementId,
  openQuickQuoteMapEngagementId,
  quickQuoteScrollTick,
  expandTransitionEngagementId,
  expandTransitionCode,
  expandAssessmentCaptureEngagementId,
  expandAssessmentCaptureTick,
  proposalPreparedViewRequest,
  proposalSurfaceInteractRequest,
}: {
  cards: CardsMessage | null;
  messageStrip: string;
  /** SAM's next reply for THIS turn hasn't arrived yet — show the same bouncing-dot indicator
   *  the chat thread shows, in place of the (now-stale) previous messageStrip text. */
  isWaiting?: boolean;
  onSave: OnSave;
  onFacet: OnFacet;
  openCards: Partial<Record<SamModule, string | null>>;
  setOpenCards: React.Dispatch<React.SetStateAction<Partial<Record<SamModule, string | null>>>>;
  /** Navigation source per module — 'list' shows a Back button; 'agent'/'recent' hide it. */
  openCardSources?: Partial<Record<SamModule, 'list' | 'agent' | 'recent'>>;
  onExpandStrip?: () => void;
  history?: RecentEntry[];
  onReplay?: (e: RecentEntry) => void;
  historyHasMore?: boolean;
  onHistoryLoadMore?: () => void;
  /** A view the USER opened without an agent turn — a manual filter/tab change on a list card, or
   *  a record created from a card's `+`. Adds it to the same "⋯" menu the agent's views land in
   *  (`useRecentViews.record` with a non-'agent' source, which also persists it). */
  onUserView?: (view: UserViewReport) => void;
  /** Push the open task detail's current fields to SAM as active_task; null clears it. */
  onActiveTask?: (task: Rec | null) => void;
  /** Push the open meeting detail's current fields to SAM as active_meeting; null clears it. */
  onActiveMeeting?: (meeting: Rec | null) => void;
  /** Push the open note detail's current fields to SAM as active_note; null clears it. */
  onNoteSynced?: (note: Rec | null) => void;
  /** Open the Note Taker pre-linked to a meeting. */
  onStartNoteTaker?: (contextType: string, entityId: string) => void;
  /** A note was deleted/saved/converted from the note detail view — refresh the on-screen notes
   *  list card so a stale record doesn't linger when the user navigates back to it. */
  onNotesListChanged?: () => void;
  /** Re-fetch the chat My To-Dos card after an in-card change (e.g. a queue claim). */
  onMyTodosChanged?: () => void;
  /** Approval ID to open inline in the workspace (from /?approval=<id> URL param).
   *  Forces the workspace into the notifications panel with the approval detail shown. */
  openApprovalId?: string | null;
  /** Called when the inline approval panel is dismissed so the caller can clear the state. */
  onCloseApproval?: () => void;
  /** Emit opened-card context when the user manually opens a card (not SAM-initiated). The agent
   *  receives a [SCREEN UPDATE] noting which item is now on screen. Pass null to clear (back). */
  onUserOpenCard?: (module: SamModule, id: string | null) => void;
  /** A list card was re-filtered by hand — sync SAM to the rows now on screen. */
  /** Switch workspace to a module (fetch rows, update panel) — used by notification entity links
   *  to actually navigate to the tasks/meetings panel after closing the notifications overlay. */
  onOpenActionView?: (module: SamModule, filter: Record<string, unknown>) => Promise<void>;
  /** Called when the user selects a notification's approval detail inside WorkspaceNotificationsPanel
   *  so the agent learns which approval is being reviewed. Null clears (back to list). */
  onApprovalSelected?: (approvalId: string | null) => void;
  /** Set by ChatPage when the user clicks the bell icon — opens the notifications workspace panel. */
  openNotificationsPanel?: boolean;
  /** A self-loading list (notifications panel) reports the rows on screen so SAM sees them. */
  onUserList?: (module: SamModule, filter: Record<string, unknown>, records: Record<string, unknown>[]) => void;
  /** Called by UXView when the notifications panel should be closed (entity link clicked, etc.). */
  onCloseNotificationsPanel?: () => void;
  /** Extra muted line under the empty-workspace message (e.g. why the last card went away). */
  emptyNote?: string;
  /** Lifted from UXView to ChatPage so mobile workspace stays visible during engagement navigation. */
  directEngagementId?: string | null;
  /** Setter for directEngagementId — called when user navigates to/from an engagement detail. */
  onDirectEngagementChange?: (engagementId: string | null) => void;
  /** Lifted from UXView to ChatPage for the same reason as directEngagementId: a plain local
   *  useState here didn't survive the mobile chat<->workspace toggle remounting UXView, so a
   *  user's engagement-dropdown pick reverted to SAM's last-pointed engagement after toggling. */
  pendingEngagement?: { propertyId: string; engagementId: string } | null;
  /** Setter for pendingEngagement — called when the user picks/clears a specific engagement. */
  onPendingEngagementChange?: (v: { propertyId: string; engagementId: string } | null) => void;
  /** The engagement_id SAM last asked to expand the Quick Quote section for
   *  (open_engagement_quick_quote tool). Passed down to WorkflowCard so it can auto-expand. */
  expandQuickQuoteEngagementId?: string | null;
  /** The engagement_id SAM last asked to open the satellite map for. Passed down to WorkflowCard
   *  so it can auto-open the map when ids match. */
  openQuickQuoteMapEngagementId?: string | null;
  /** Incremented on every REFRESH_QUICK_QUOTE — triggers a scroll to the Quick Quote section
   *  even when the expandQuickQuoteEngagementId hasn't changed. */
  quickQuoteScrollTick?: number;
  /** The engagement_id SAM last asked to open a blocked transition's business_capture form for
   *  (EXPAND_ENGAGEMENT_TRANSITION_CAPTURE). */
  expandTransitionEngagementId?: string | null;
  /** The transition_code to open — paired with expandTransitionEngagementId. */
  expandTransitionCode?: string | null;
  /** The engagement_id SAM last asked to open the Assessment capture card for
   *  (EXPAND_ENGAGEMENT_ASSESSMENT_CAPTURE). */
  expandAssessmentCaptureEngagementId?: string | null;
  /** Bumped on every EXPAND_ENGAGEMENT_ASSESSMENT_CAPTURE to re-trigger the expand+flash even
   *  when the engagement_id hasn't changed. */
  expandAssessmentCaptureTick?: number;
  /** Last proposal_prepared_open request from SAM — open the named panel on the Proposal Prepared card. */
  proposalPreparedViewRequest?: { engagementId: string; view: 'preview_proposal' | 'view_estimate' | 'open_assessment'; tick: number } | null;
  /** Last proposal_surface_interact request from SAM — an interaction inside an already-open surface. */
  proposalSurfaceInteractRequest?: ProposalSurfaceInteractPayload & { tick: number } | null;
}) => {
  // Track strip changes to replay the highlight animation on each new SAM response.
  const prevStripRef = useRef('');
  const prevWaitingRef = useRef(false);
  const [stripKey, setStripKey] = useState(0);
  const [isWorkspaceRefreshing, setIsWorkspaceRefreshing] = useState(false);
  // Set when the user clicks a specific engagement sub-card in the Properties list (a plain
  // local click, not a SAM action) — lands WorkspacePropertyDetail directly on that engagement
  // instead of its Engagement tab's multi-engagement picker. cards.filter?.initial_engagement_id
  // (set by SAM's get_engagement) still takes priority when both are present.
  // Scoped to its source property so that opening a different property via SAM never inherits
  // a stale engagement ID from a previous sub-card click on a different property.
  // Lifted to ChatPage (like directEngagementId below) so it survives the mobile chat<->workspace
  // toggle remounting this component — see the props doc comment for the bug this fixed.
  const pendingEngagement = pendingEngagementProp;
  const setPendingEngagement = onPendingEngagementChange ?? (() => {});
  // Passed to WorkspacePropertyDetail's own engagement pickers (header dropdown, card-picker
  // list) via PendingEngagementContext — see that context's declaration for why they must write
  // here rather than to their own local state.
  const pendingEngagementCtxValue = useMemo(
    () => ({ pendingEngagement, setPendingEngagement }),
    [pendingEngagement, setPendingEngagement],
  );
  // The "SAM repointed, so the earlier click is stale" clear (once here as a local useEffect) is
  // now owned by ChatPage, alongside the lifted state itself — see the comment there. A local
  // effect here would fire on every fresh mount of THIS component (the mobile chat<->workspace
  // toggle remounts it), unconditionally wiping the just-lifted pick right back out, since a
  // brand-new mount's effect has no "previous" samPickedEngagement to diff against.
  // directEngagementId is lifted to ChatPage so mobile workspace stays visible during navigation.
  const directEngagementId = directEngagementIdProp;
  const setDirectEngagementId = onDirectEngagementChange ?? (() => {});
  // The task/meeting/property lists seed their chips and search from `initial*` props ONCE, at
  // mount, so they must remount whenever a new view lands on them. The filter covers SAM moving
  // to a different filter; the card id covers a "⋯" replay (replayView always mints a fresh card),
  // which may carry the SAME filter the list opened with — keyed on the filter alone, the chips
  // the user had changed since stayed on screen and the replay looked like a no-op.
  const { t: tCards } = useTranslation();
  const { cards: cardCatalogue, isError: cardCatalogueError } = useCardCatalogueState();
  const listKey = cards ? `${cards.id}:${JSON.stringify(cards.filter ?? {})}` : '';
  useEffect(() => {
    if (messageStrip && messageStrip !== prevStripRef.current) {
      prevStripRef.current = messageStrip;
      setStripKey(k => k + 1);
    } else if (isWaiting && !prevWaitingRef.current) {
      // The strip can also first appear with no messageStrip yet (dots-only, isWaiting just
      // turned true) — that's a new event too, so replay the glow here as well.
      setStripKey(k => k + 1);
    }
    prevWaitingRef.current = !!isWaiting;
  }, [messageStrip, isWaiting]);

  // Phone only: the workspace header (title row; the reply strip stays) lives INSIDE the scroller, so
  // it scrolls away with the content and returns when the user scrolls back to the top. Desktop keeps
  // it pinned. The calendar scrolls internally, so its header stays pinned too.
  const isDesktopView = useIsDesktop();
  const headerInScroller = !isDesktopView && cards?.module !== 'calendar';

  // Card component registry — which component renders a workspace card is chosen by the backend
  // card catalogue (cards/catalogue.py → ui_component of the module's list/panel card), not by a
  // hard-coded module chain. Each renderer owns its module's list AND detail (openCards) views.
  const renderCardBody = (cards: CardsMessage): React.ReactNode => {
    const renderers: Record<string, () => React.ReactNode> = {
      TaskList: () => (
          <TaskList
            key={listKey}
            initialStatus={cards.filter?.status as string | undefined}
            initialPriority={cards.filter?.priority as string | undefined}
            initialAssigneeUserId={cards.filter?.assignee_user_id as string | undefined}
            initialOwnerUserId={cards.filter?.owner_user_id as string | undefined}
            initialUpdatedByUserId={cards.filter?.updated_by_user_id as string | undefined}
            initialSearch={cards.filter?.search as string | undefined}
            initialOverdue={!!cards.filter?.overdue}
            initialUnassigned={!!cards.filter?.unassigned}
            // Every filter search_tasks can publish has to land here. Anything missing is dropped
            // silently and the panel then contradicts the answer SAM just gave — "tasks due on the
            // 18th" replied with 1 while the list underneath still showed all 8.
            initialFollowerUserId={cards.filter?.follower_user_id as string | undefined}
            initialDueDateFrom={cards.filter?.due_date_from as string | undefined}
            initialDueDateTo={cards.filter?.due_date_to as string | undefined}
            initialQueueRoleId={cards.filter?.queue_role_id as string | undefined}
            initialContextType={cards.filter?.context_type as string | undefined}
            initialContextEntityId={cards.filter?.context_entity_id as string | undefined}
            initialIds={cards.filter?.ids as string | undefined}
            initialMine={typeof cards.filter?.mine === 'boolean' ? cards.filter.mine : true}
            initialRecords={cards.records as unknown as TaskFull[]}
            singleTaskId={openCards['tasks'] ?? undefined}
            onExitSingleTask={() => setOpenCards(prev => ({ ...prev, tasks: null }))}
            initialSortBy={cards.filter?.sort_by as string | undefined}
            initialSortDir={cards.filter?.sort_dir as number | undefined}
            initialAdvancedFilters={{
              exclude_assignee_user_id: cards.filter?.exclude_assignee_user_id as string | undefined,
              created_from: cards.filter?.created_from as string | undefined,
              created_to: cards.filter?.created_to as string | undefined,
              no_due_date: !!cards.filter?.no_due_date,
              not_overdue: !!cards.filter?.not_overdue,
              title_starts_with: cards.filter?.title_starts_with as string | undefined,
            }}
            onFiltersChange={(params) => onUserView?.({ module: 'tasks', params })}
          />
        ),
      NoteWorkspace: () => (
          openCards['notes'] ? (
            <WorkspaceNoteDetail
              noteId={openCards['notes']}
              onBack={() => { setOpenCards(prev => ({ ...prev, notes: null })); onUserOpenCard?.('notes', null); }}
              onOpenTask={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
              onNoteSynced={onNoteSynced}
              onNotesListChanged={onNotesListChanged}
            />
          ) : (
            <InlineCards
              module={cards.module}
              records={cards.records as Rec[]}
              facets={cards.facets}
              onSave={onSave}
              onFacet={onFacet}
              openedId={openCards[cards.module]}
              onOpenChange={id => { setOpenCards(prev => ({ ...prev, [cards.module]: id })); onUserOpenCard?.(cards.module, id); }}
            />
          )
        ),
      WorkspaceMeetingList: () => (
          <WorkspaceMeetingList
            key={listKey}
            initialStatus={cards.filter?.status as string | undefined}
            initialMeetingType={cards.filter?.meeting_type as string | undefined}
            initialSearch={cards.filter?.search as string | undefined}
            initialOrganizerUserId={cards.filter?.organizer_user_id as string | undefined}
            initialUpdatedByUserId={cards.filter?.updated_by_user_id as string | undefined}
            initialStartFrom={cards.filter?.start_from as string | undefined}
            initialStartTo={cards.filter?.start_to as string | undefined}
            initialContextType={cards.filter?.context_type as string | undefined}
            initialContextEntityId={cards.filter?.context_entity_id as string | undefined}
            initialIds={cards.filter?.ids as string | undefined}
            initialExpandedId={openCards['meetings'] ?? undefined}
            initialAdvancedFilters={{
              exclude_status: cards.filter?.exclude_status as string | undefined,
              exclude_meeting_type: cards.filter?.exclude_meeting_type as string | undefined,
              title_starts_with: cards.filter?.title_starts_with as string | undefined,
              participant_user_id: cards.filter?.participant_user_id as string | undefined,
              participant: cards.filter?.participant as string | undefined,
              participant_contact_id: cards.filter?.contact_id as string | undefined,
              min_participants: cards.filter?.min_participants as number | undefined,
              upcoming: !!cards.filter?.upcoming,
              past: !!cards.filter?.past,
              limit: cards.filter?.limit as number | undefined,
            }}
            singleMeetingId={openCards['meetings'] ?? undefined}
            onExitSingleMeeting={() => setOpenCards(prev => ({ ...prev, meetings: null }))}
            initialSortBy={cards.filter?.sort_by as string | undefined}
            initialSortDir={cards.filter?.sort_dir as number | undefined}
            onStartNoteTaker={onStartNoteTaker}
            onFiltersChange={(params) => onUserView?.({ module: 'meetings', params })}
          />
        ),
      MyTodosList: () => (
          <MyTodosList
            records={cards.records as Rec[]}
            onSelectTask={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
            onSelectMeeting={(id) => { setOpenCards(prev => ({ ...prev, my_todos: id })); onUserOpenCard?.('my_todos', id); }}
            onChanged={onMyTodosChanged}
          />
        ),
      CalendarPage: () => (
          <CalendarPage
            onTaskClick={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
            onMeetingClick={(id) => { setOpenCards(prev => ({ ...prev, meetings: id })); onUserOpenCard?.('meetings', id); }}
          />
        ),
      PropertyWorkspace: () => (
          openCards['properties'] ? (
            <WorkspacePropertyDetail
              key={openCards['properties']}
              propertyId={openCards['properties']}
              // Two sources, most-recent-intent first:
              //   pendingEngagement — the user just clicked a specific engagement row in the
              //     property list. This is checked FIRST: it used to lose to the filter below,
              //     so on a property with two engagements, clicking "Replacement 2" opened
              //     whichever one SAM had last pointed at instead. A click now is newer than a
              //     filter set earlier in the conversation, the same reasoning the "+ new
              //     engagement" pick already uses against initialEngagementId.
              //   cards.filter.initial_engagement_id — set by get_engagement (record_id=
              //     property_id + filter) when the user NAMED a specific engagement to SAM.
              //     Absent for a plain search_engagement panel view.
              initialEngagementId={
                (pendingEngagement && pendingEngagement.propertyId === openCards['properties'] ? pendingEngagement.engagementId : undefined)
                ?? (cards.filter?.initial_engagement_id as string | undefined)
                ?? undefined
              }
              hideBackButton={openCardSources?.['properties'] !== 'list'}
              onBack={() => {
                setOpenCards(prev => ({ ...prev, properties: null }));
                setPendingEngagement(null);
                onUserOpenCard?.('properties', null);
              }}
              onSelectTask={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
              expandQuickQuoteEngagementId={expandQuickQuoteEngagementId}
              openQuickQuoteMapEngagementId={openQuickQuoteMapEngagementId}
              quickQuoteScrollTick={quickQuoteScrollTick}
              expandTransitionEngagementId={expandTransitionEngagementId}
              expandTransitionCode={expandTransitionCode}
              expandAssessmentCaptureEngagementId={expandAssessmentCaptureEngagementId}
              expandAssessmentCaptureTick={expandAssessmentCaptureTick}
              proposalPreparedViewRequest={proposalPreparedViewRequest}
              proposalSurfaceInteractRequest={proposalSurfaceInteractRequest}
            />
          ) : (
            <WorkspacePropertyList
              key={listKey}
              initialSearch={cards.filter?.search as string | undefined}
              initialEngagementStatus={cards.filter?.engagement_status as 'active' | 'all' | undefined}
              initialStatus={cards.filter?.status as string | undefined}
              initialCity={cards.filter?.city as string | undefined}
              initialState={cards.filter?.state as string | undefined}
              initialPostalCode={cards.filter?.postal_code as string | undefined}
              initialPropertyType={cards.filter?.property_type as 'residential' | 'commercial' | 'industrial' | undefined}
              initialPropertyStatus={cards.filter?.property_status as string | undefined}
              initialEngagementType={cards.filter?.engagement_type as string | undefined}
              initialSource={cards.filter?.source as string | undefined}
              initialCreatedAfter={cards.filter?.created_after as string | undefined}
              initialCreatedBefore={cards.filter?.created_before as string | undefined}
              initialUpdatedAfter={cards.filter?.updated_after as string | undefined}
              initialUpdatedBefore={cards.filter?.updated_before as string | undefined}
              initialAssignee={cards.filter?.assignee as string | undefined}
              initialAssigneeLabel={cards.filter?.assignee_label as string | undefined}
              initialAssigneeRole={cards.filter?.assignee_role as 'sales_agent' | 'project_manager' | 'assigned_user' | undefined}
              initialContactName={cards.filter?.contact_name as string | undefined}
              initialContactRole={cards.filter?.contact_role as string | undefined}
              onSelectProperty={(id) => {
                setOpenCards(prev => ({ ...prev, properties: id }));
                onUserOpenCard?.('properties', id);
              }}
              onSelectEngagement={(propertyId, engagementId) => {
                setPendingEngagement({ propertyId, engagementId });
                setOpenCards(prev => ({ ...prev, properties: propertyId }));
                onUserOpenCard?.('properties', propertyId);
              }}
              onFiltersChange={(params) => onUserView?.({ module: 'properties', params })}
            />
          )
        ),
      EngagementWorkspace: () => (
          openCards['engagements'] ? (
            <EngagementAsPropertyDetail
              engagementId={openCards['engagements']}
              records={cards.records as Rec[]}
              onBack={() => { setOpenCards(prev => ({ ...prev, engagements: null })); onUserOpenCard?.('engagements', null); }}
              onSelectTask={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
              expandQuickQuoteEngagementId={expandQuickQuoteEngagementId}
              openQuickQuoteMapEngagementId={openQuickQuoteMapEngagementId}
              quickQuoteScrollTick={quickQuoteScrollTick}
              expandTransitionEngagementId={expandTransitionEngagementId}
              expandTransitionCode={expandTransitionCode}
              expandAssessmentCaptureEngagementId={expandAssessmentCaptureEngagementId}
              expandAssessmentCaptureTick={expandAssessmentCaptureTick}
            />
          ) : (
            <InlineCards
              module={cards.module}
              records={cards.records as Rec[]}
              facets={cards.facets}
              onSave={onSave}
              onFacet={onFacet}
              openedId={null}
              onOpenChange={id => { setOpenCards(prev => ({ ...prev, engagements: id })); onUserOpenCard?.('engagements', id); }}
            />
          )
        ),
      InlineCards: () => (
          <InlineCards
            module={cards.module}
            records={cards.records as Rec[]}
            facets={cards.facets}
            onSave={onSave}
            onFacet={onFacet}
            openedId={openCards[cards.module]}
            onOpenChange={id => { setOpenCards(prev => ({ ...prev, [cards.module]: id })); onUserOpenCard?.(cards.module, id); }}
          />
        ),
    };
    if (cardCatalogueError) {
      return <p className="py-12 text-center text-sm text-ui-text2">{tCards('chat.cardsUnavailable')}</p>;
    }
    if (!cardCatalogue) return <div className="flex justify-center py-12"><Spinner /></div>;
    const definition = resolveCardDefinition(cardCatalogue, cards.module, null);
    const render = definition ? renderers[definition.ui_component] : undefined;
    if (definition && !render) {
      console.warn(`[cards] no renderer for ui_component "${definition.ui_component}" (${definition.id}) — using InlineCards`);
    }
    return (render ?? renderers.InlineCards)();
  };

  const workspaceHeaderNode = (
    <>
    {/* Workspace header — title + refresh button + history menu (⋯) for all modules */}
    <WorkspaceHeader
      title={(() => {
        const headerLabel = openApprovalId && !openNotificationsPanel && cards?.module !== 'notifications' ? 'Approval Review' : openNotificationsPanel || cards?.module === 'notifications' ? 'Notifications' : directEngagementId ? 'Engagement' : cards ? (cards.title || humanizeLabel(cards.module)) : 'Workspace';
        const detailIsOpen = cards && !openApprovalId && !openNotificationsPanel && !directEngagementId && openCards[cards.module];
        // When a detail is open, show the record's name in the header (breadcrumb style)
        // so the user knows what they're viewing. Fall back to the list title if not found.
        const detailTitle = detailIsOpen
          ? history.find(e => e.cardType === cards.module && e.contextRef?.id === openCards[cards.module])?.title
          : undefined;
        return detailTitle || headerLabel;
      })()}
      actions={
        <>
          {cards && (
            <WorkspaceRefreshButton
              module={cards.module}
              openId={openCards[cards.module]}
              onRefreshChange={setIsWorkspaceRefreshing}
            />
          )}
          <HistoryMenu history={history} caption={cards?.caption} onReplay={onReplay} hasMore={historyHasMore} onLoadMore={onHistoryLoadMore} />
        </>
      }
    />
    {/* Refresh progress bar — thin shimmer strip visible while workspace is reloading */}
    <div className="flex-shrink-0 relative h-0.5 overflow-hidden bg-transparent">
      {isWorkspaceRefreshing && (
        <div className="workspace-refresh-bar bg-accent opacity-75" />
      )}
    </div>
    </>
  );
  return (
  <UserViewContext.Provider value={onUserView}>
  <PendingEngagementContext.Provider value={pendingEngagementCtxValue}>
  <div className="flex flex-col h-full min-h-0">
    {/* Agent reply strip — glowing border highlight on each new SAM response. Shows even with no
        messageStrip yet when isWaiting, so the dots below appear as soon as the turn starts. */}
    {(messageStrip || isWaiting) && (
      <div
        key={stripKey}
        onClick={isWaiting ? undefined : onExpandStrip}
        role={!isWaiting && onExpandStrip ? 'button' : undefined}
        className={`flex-shrink-0 w-full text-left px-2 py-1 border-b border-ui-border bg-ui-subtle ${stripKey > 0 ? 'sam-reply-new' : ''}`}
      >
        <div
          className={`rounded-chip border border-ui-border border-l-[3px] border-l-accent/40 bg-ui-card px-2.5 py-1.5 ${!isWaiting && onExpandStrip ? 'cursor-pointer hover:bg-ui-hover' : ''}`}
        >
          {isWaiting ? (
            <TypingDots />
          ) : (
            <>
              <div className="text-sm text-ui-text leading-snug overflow-hidden max-h-20">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc pl-4 mb-1 space-y-0.5">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-4 mb-1 space-y-0.5">{children}</ol>,
                    li: ({ children }) => <li className="leading-snug">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold text-ui-text">{children}</strong>,
                    em: ({ children }) => <em className="italic text-ui-text2">{children}</em>,
                    h1: ({ children }) => <p className="font-bold mb-1">{children}</p>,
                    h2: ({ children }) => <p className="font-bold mb-1">{children}</p>,
                    h3: ({ children }) => <p className="font-semibold mb-1">{children}</p>,
                    a: ({ href, children }) => <a href={href} className="underline text-accent-text hover:text-accent-text" target="_blank" rel="noopener noreferrer">{children}</a>,
                    code: ({ children }) => <code className="bg-ui-subtle rounded px-1 text-xs font-mono">{children}</code>,
                  }}
                >
                  {messageStrip}
                </ReactMarkdown>
              </div>
              {onExpandStrip && <span className="mt-0.5 inline-block text-xs text-accent-text">Tap to open chat</span>}
            </>
          )}
        </div>
      </div>
    )}
    {!headerInScroller && workspaceHeaderNode}
<div className={`flex-1 min-h-0 ${cards?.module === 'calendar' ? 'overflow-hidden flex flex-col' : 'overflow-y-auto no-scrollbar px-2 py-1'}`}>
      {headerInScroller && <div className="-mx-2 -mt-1">{workspaceHeaderNode}</div>}
      {/* Notifications panel — shown from: bell icon (openNotificationsPanel), approval URL param
          (openApprovalId), or SAM opening the notifications module (cards.module === 'notifications').
          All three cases render the same panel; it handles approvals inline via externalApprovalId. */}
      {openApprovalId || openNotificationsPanel || cards?.module === 'notifications' ? (
        <WorkspaceNotificationsPanel
          externalApprovalId={openApprovalId}
          onExternalApprovalClose={onCloseApproval}
          onApprovalSelected={onApprovalSelected}
          onRowsChange={(filter, rows) => onUserList?.('notifications', filter, rows)}
          onEntityLink={(module, entityId, entityType) => {
            onCloseNotificationsPanel?.();
            const mod = module?.toUpperCase();
            if (mod === 'TSK' || mod === 'TASK') {
              setOpenCards(prev => ({ ...prev, tasks: entityId }));
              void onOpenActionView?.('tasks', {});
            } else if (mod === 'MTG' || mod === 'MEETING') {
              setOpenCards(prev => ({ ...prev, meetings: entityId }));
              void onOpenActionView?.('meetings', {});
            } else if (module === 'WFL' && entityType === 'engagement') {
              setDirectEngagementId(entityId);
            } else if (mod === 'COM') {
              onUserOpenCard?.('communications', entityId);
            } else if (mod === 'USR') {
              onUserOpenCard?.('users', entityId);
            } else {
              onUserOpenCard?.(module as SamModule, entityId);
            }
          }}
        />
      ) : directEngagementId ? (
        <WorkspaceDirectEngagementDetail
          engagementId={directEngagementId}
          onBack={() => setDirectEngagementId(null)}
          onSelectTask={(id) => { setDirectEngagementId(null); setOpenCards(prev => ({ ...prev, tasks: id })); onUserOpenCard?.('tasks', id); }}
          expandQuickQuoteEngagementId={expandQuickQuoteEngagementId}
          openQuickQuoteMapEngagementId={openQuickQuoteMapEngagementId}
          quickQuoteScrollTick={quickQuoteScrollTick}
          expandTransitionEngagementId={expandTransitionEngagementId}
          expandTransitionCode={expandTransitionCode}
          expandAssessmentCaptureEngagementId={expandAssessmentCaptureEngagementId}
          expandAssessmentCaptureTick={expandAssessmentCaptureTick}
        />
      ) : cards ? renderCardBody(cards) : (
        <div className="flex flex-col items-center justify-center h-full text-center gap-2 px-6">
          <p className="text-ui-text2 text-sm font-medium">{tCards('chat.workspaceEmpty.title')}</p>
          <p className="text-ui-text3 text-xs">{tCards('chat.workspaceEmpty.hint')}</p>
          {emptyNote && <p className="text-ui-text3 text-xs italic">{emptyNote}</p>}
        </div>
      )}
    </div>
  </div>
  </PendingEngagementContext.Provider>
  </UserViewContext.Provider>
  );
};

export default UXView;
